/**
 * Generated by orval v8.8.1 🍺
 * Do not edit manually.
 * Posts API
 * API for managing posts
 * OpenAPI spec version: 1.0.0
 */
import * as zod from "zod";

/**
 * Upload image or video file for WYSIWYG editor. Returns URL for inserted media.
 * @summary Upload media file (image or video)
 */
export const uploadMediaBodyBoardIdDefault = `default`;
export const uploadMediaBodyBoardIdMax = 50;

export const UploadMediaBody = zod
    .object({
        file: zod.instanceof(File).describe("Image or video file to upload"),
        board_id: zod
            .string()
            .max(uploadMediaBodyBoardIdMax)
            .default(uploadMediaBodyBoardIdDefault)
            .describe("Board ID for organizing uploaded files"),
        type: zod.enum(["image", "video"]).describe("Type of media file"),
    })
    .strict();

export const UploadMedia201Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).describe("Success status"),
        url: zod.string().url().describe("Public URL of uploaded media"),
        filename: zod.string().describe("Generated filename"),
        type: zod.enum(["image", "video"]).describe("Type of uploaded media"),
    })
    .strict();

export const UploadMedia422Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        message: zod.string().optional(),
        status_code: zod.literal(422).optional(),
        errors: zod.record(zod.string(), zod.array(zod.string())).optional(),
    })
    .strict();

export const UploadMedia500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        message: zod.string().optional(),
        errors: zod.object({}).passthrough().optional(),
    })
    .strict();

/**
 * Delete a previously uploaded editor media file.
 * @summary Delete media file
 */
export const deleteMediaBodyFilenameMax = 255;

export const deleteMediaBodyBoardIdMax = 50;

export const DeleteMediaBody = zod
    .object({
        filename: zod.string().max(deleteMediaBodyFilenameMax),
        board_id: zod.string().max(deleteMediaBodyBoardIdMax),
    })
    .strict();

export const DeleteMedia200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        message: zod.string().optional(),
    })
    .strict();

export const DeleteMedia404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        message: zod.string().optional(),
    })
    .strict();

export const DeleteMedia422Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        message: zod.string().optional(),
        status_code: zod.literal(422).optional(),
        errors: zod.record(zod.string(), zod.array(zod.string())).optional(),
    })
    .strict();

export const DeleteMedia500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        message: zod.string().optional(),
    })
    .strict();

/**
 * @summary Create new board post
 */
export const CreateBoardPostParams = zod
    .object({
        board_id: zod.enum([
            "review",
            "tiktoks",
            "beauty_news",
            "editorial_reviews",
            "expert_reviews",
            "activities",
            "test_modal",
            "test_modeless",
            "guidance",
        ]),
    })
    .strict();

export const CreateBoardPostHeader = zod
    .object({
        "Content-Type": zod.enum(["multipart/form-data"]).describe("Content type of the request body"),
        Accept: zod.enum(["application/json"]).describe("Content type that client accepts"),
    })
    .strict();

export const createBoardPostBodyPostTitleMax = 300;

export const createBoardPostBodyAttachedImagesMax = 10;

export const createBoardPostBodyAttachedVideosMax = 1;

export const CreateBoardPostBody = zod
    .object({
        post_title: zod.string().max(createBoardPostBodyPostTitleMax).optional(),
        content: zod.string().min(1),
        post_product_id: zod.number().nullish().describe("Product ID for review posts (required for review board)"),
        sns_shares: zod
            .array(zod.enum(["instagram", "tiktok"]))
            .optional()
            .describe("SNS share destinations requested for the post."),
        review_data: zod
            .object({
                post_product_id: zod.number().nullish().describe("Product ID associated with the review."),
                review_config_id: zod.number().optional().describe("Review config ID that defines the question set."),
                answers: zod
                    .record(
                        zod.string(),
                        zod.union([
                            zod.string(),
                            zod.number(),
                            zod.number(),
                            zod.array(zod.union([zod.string(), zod.number(), zod.number()])),
                        ]),
                    )
                    .optional()
                    .describe("Review answers keyed by review question ID from HTML form fields."),
            })
            .strict()
            .optional()
            .describe("Review metadata and answers used when creating review posts."),
        attached_images: zod
            .array(zod.instanceof(File))
            .max(createBoardPostBodyAttachedImagesMax)
            .optional()
            .describe("Optional images to attach to the post"),
        attached_videos: zod
            .array(zod.instanceof(File))
            .max(createBoardPostBodyAttachedVideosMax)
            .optional()
            .describe("Optional videos to attach to the post"),
        content_images: zod.string().optional().describe("JSON-encoded editor embedded image filenames"),
        attachment_ids: zod
            .array(zod.number())
            .optional()
            .describe("Optional attachment IDs uploaded beforehand via \/attachments"),
        sns_links: zod
            .array(zod.string().url())
            .optional()
            .describe("Optional SNS links (Facebook, YouTube, etc.) extracted from content"),
        created_at: zod
            .string()
            .date()
            .optional()
            .describe(
                "Override post created_at date (admin\/editor only). Stored as start-of-day. If omitted, server uses current time.",
            ),
        post_status: zod
            .enum(["draft", "editing", "released", "rejected"])
            .optional()
            .describe('Post visibility status (admin\/editor only). If omitted, server defaults to \"released\".'),
    })
    .strict();

export const CreateBoardPost201Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).describe("Success flag: 1 = success, 0 = failure"),
        post_id: zod.number().describe("Newly created post ID for rendering"),
        message: zod.string().describe("Success message shown after post creation"),
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .describe("Board identifier where the post was created"),
        redirect_url: zod.string().url().describe("Redirect destination after successful post creation"),
    })
    .strict();

export const CreateBoardPost400Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(422)])
            .describe("HTTP status code"),
    })
    .strict();

export const CreateBoardPost403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(422)])
            .describe("HTTP status code"),
    })
    .strict();

export const CreateBoardPost404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(422)])
            .describe("HTTP status code"),
    })
    .strict();

export const CreateBoardPost422Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(422)])
            .describe("HTTP status code"),
    })
    .strict();

/**
 * @summary Get board list with search and filtering
 */
export const GetBoardListParams = zod
    .object({
        board_id: zod.enum([
            "review",
            "tiktoks",
            "beauty_news",
            "editorial_reviews",
            "expert_reviews",
            "activities",
            "test_modal",
            "test_modeless",
            "guidance",
        ]),
    })
    .strict();

export const getBoardListQuerySearchMax = 100;

export const getBoardListQueryQMax = 100;

export const getBoardListQueryContentSearchMax = 200;

export const getBoardListQueryContentSearchFieldsMax = 100;

export const getBoardListQueryPageDefault = 1;

export const getBoardListQueryPerPageDefault = 20;
export const getBoardListQueryPerPageMax = 100;

export const getBoardListQuerySortDefault = `created_at`;
export const getBoardListQueryOrderDefault = `desc`;
export const getBoardListQueryProductIdMin = 0;

export const getBoardListQueryLinkedProductStatusMax = 20;

export const getBoardListQueryRecommendedOnlyDefault = 0;

export const GetBoardListQueryParams = zod
    .object({
        search: zod
            .string()
            .max(getBoardListQuerySearchMax)
            .optional()
            .describe("Search keyword for post title or content"),
        q: zod.string().max(getBoardListQueryQMax).optional().describe("Alias of `search`"),
        content_search: zod
            .string()
            .max(getBoardListQueryContentSearchMax)
            .optional()
            .describe("Keyword search for posts using selected target fields (title, content)"),
        content_search_fields: zod
            .string()
            .max(getBoardListQueryContentSearchFieldsMax)
            .optional()
            .describe("Comma-separated list of post target fields to search (post_title, content)"),
        post_status: zod
            .enum(["draft", "editing", "released", "rejected"])
            .optional()
            .describe("Filter by post status (admin\/editor only)"),
        reviewed_only: zod
            .union([zod.literal(0), zod.literal(1)])
            .optional()
            .describe("If 1, filter to posts on the review board only (board_id = review)"),
        exclude_text_only: zod
            .union([zod.literal(0), zod.literal(1)])
            .optional()
            .describe("If 1, exclude posts with no media (no images, videos, or SNS links)"),
        page: zod.number().min(1).default(getBoardListQueryPageDefault).describe("Page number for pagination"),
        per_page: zod
            .number()
            .min(1)
            .max(getBoardListQueryPerPageMax)
            .default(getBoardListQueryPerPageDefault)
            .describe("Number of items per page"),
        sort: zod
            .enum(["created_at", "updated_at", "post_id", "recommend_rank", "monthly_rank"])
            .default(getBoardListQuerySortDefault)
            .describe("Sort field for posts"),
        order: zod.enum(["asc", "desc"]).default(getBoardListQueryOrderDefault).describe("Sort order"),
        user_id: zod
            .number()
            .min(1)
            .optional()
            .describe(
                "Filter by user ID (supports multiple values; comma-separated values are also accepted by implementation)",
            ),
        user: zod
            .union([zod.number(), zod.array(zod.number())])
            .optional()
            .describe("Alias of `user_id` (supports multiple values)"),
        product_id: zod
            .number()
            .min(getBoardListQueryProductIdMin)
            .optional()
            .describe(
                "Filter by product ID (supports multiple values; comma-separated values are also accepted by implementation). 0 is a valid value — the NOTFOUND sentinel for review posts with no product or brand resolved (see ReviewImportService \/ ISS-02-209).",
            ),
        linked_product_status: zod
            .string()
            .max(getBoardListQueryLinkedProductStatusMax)
            .optional()
            .describe(
                'Filter to review posts whose linked product has this product_status (e.g. \"brand\" — posts linked to a brand\'s product_status=brand placeholder instead of a specific product, see ISS-02-209). Counterpart of exclude_product_statuses.',
            ),
        product_ids: zod
            .union([zod.number(), zod.array(zod.number())])
            .optional()
            .describe("Alias of `product_id` (supports multiple values)"),
        category_id: zod
            .number()
            .min(1)
            .nullish()
            .describe(
                "Filter by product category ID (supports multiple values; comma-separated values are also accepted by implementation)",
            ),
        category_ids: zod
            .union([zod.number(), zod.array(zod.number())])
            .optional()
            .describe("Alias of `category_id` (supports multiple values)"),
        parent_category_id: zod
            .number()
            .nullish()
            .describe(
                "Filter by parent category ID (expands to child category_id list; comma-separated values are also accepted by implementation)",
            ),
        parent_category_ids: zod
            .union([zod.number(), zod.array(zod.number())])
            .optional()
            .describe("Alias of `parent_category_id` (supports multiple values)"),
        brand_id: zod
            .number()
            .min(1)
            .nullish()
            .describe(
                "Filter by linked product brand ID (supports multiple values; comma-separated values are also accepted by implementation)",
            ),
        brand_ids: zod
            .union([zod.number(), zod.array(zod.number())])
            .optional()
            .describe("Alias of `brand_id` (supports multiple values)"),
        recommended_only: zod
            .union([zod.literal(0), zod.literal(1)])
            .default(getBoardListQueryRecommendedOnlyDefault)
            .describe("Return only posts with recommend_rank > 0"),
        reccommended_only: zod
            .union([zod.literal(0), zod.literal(1)])
            .optional()
            .describe("Backward-compatible alias of recommended_only"),
    })
    .strict();

export const getBoardList200ResponseDataItemPostTitleMax = 300;

export const getBoardList200ResponseDataItemRecommendRankMax = 15;

export const getBoardList200ResponseDataItemCommentsItemUserDisplayNameMax = 50;

export const getBoardList200ResponseDataItemCommentsItemUserFirstNameMax = 100;

export const getBoardList200ResponseDataItemCommentsItemUserLastNameMax = 100;

export const getBoardList200ResponseDataItemCommentsItemUserNickNameMax = 50;

export const getBoardList200ResponseDataItemCommentsItemUserPreferredPriceRangeMax = 50;

export const getBoardList200ResponseDataItemCommentsItemUserSocialInstagramMax = 100;

export const getBoardList200ResponseDataItemCommentsItemUserSocialFacebookMax = 100;

export const getBoardList200ResponseDataItemCommentsItemUserSocialXMax = 100;

export const getBoardList200ResponseDataItemCommentsItemUserSocialTiktokMax = 100;

export const getBoardList200ResponseDataItemCommentsItemUserSocialYoutubeMax = 100;

export const getBoardList200ResponseDataItemCommentsItemUserSocialWebsiteMax = 500;

export const getBoardList200ResponseDataItemCommentsItemUserAvatarMax = 255;

export const getBoardList200ResponseDataItemReviewConfigOneReviewConfigNameMax = 70;

export const getBoardList200ResponseDataItemReviewQuestionsItemQuestionTextThMax = 200;

export const getBoardList200ResponseDataItemReviewQuestionsItemQuestionTextEnMax = 200;

export const getBoardList200ResponseDataItemReviewQuestionsItemQuestionTextJaMax = 200;

export const getBoardList200ResponseDataItemReviewDataOneAnswersItemQuestionTextThMax = 200;

export const getBoardList200ResponseDataItemReviewDataOneAnswersItemQuestionTextEnMax = 200;

export const getBoardList200ResponseDataItemReviewDataOneAnswersItemQuestionTextJaMax = 200;

export const getBoardList200ResponseDataItemAdsItemImagePathMax = 150;

export const getBoardList200ResponseDataItemAdsItemAdTitleMax = 40;

export const getBoardList200ResponseDataItemAdsItemAdBodyMax = 130;

export const getBoardList200ResponseDataItemAdsItemLinkUrlMax = 500;

export const getBoardList200ResponseDataItemProductOneProductNameMax = 255;

export const getBoardList200ResponseDataItemProductOneBrandNameMax = 100;

export const getBoardList200ResponseDataItemProductOneBrandBrandNameMax = 100;

export const getBoardList200ResponseDataItemProductOneCategoryNameMax = 100;

export const getBoardList200ResponseDataItemProductOneCategoryCategoryNameMax = 100;

export const getBoardList200ResponseDataItemProductOneCategoryParentCategoryNameMax = 100;

export const getBoardList200ResponseDataItemProductOneImagePathMax = 150;

export const getBoardList200ResponseDataItemProductOneReviewCountDefault = 0;
export const getBoardList200ResponseDataItemProductOnePriceUnitMax = 10;

export const getBoardList200ResponseDataItemProductOneUnitMax = 100;

export const getBoardList200ResponseDataItemProductOneKeywordMax = 500;

export const getBoardList200ResponseDataItemProductOneParentCategoryNameMax = 100;

export const getBoardList200ResponseDataItemProductOneReviewSummaryEnMax = 500;

export const getBoardList200ResponseDataItemProductOneReviewSummaryJaMax = 500;

export const getBoardList200ResponseDataItemRecommendedProductsItemProductNameMax = 255;

export const getBoardList200ResponseDataItemRecommendedProductsItemBrandNameMax = 100;

export const getBoardList200ResponseDataItemRecommendedProductsItemBrandBrandNameMax = 100;

export const getBoardList200ResponseDataItemRecommendedProductsItemCategoryNameMax = 100;

export const getBoardList200ResponseDataItemRecommendedProductsItemCategoryCategoryNameMax = 100;

export const getBoardList200ResponseDataItemRecommendedProductsItemCategoryParentCategoryNameMax = 100;

export const getBoardList200ResponseDataItemRecommendedProductsItemImagePathMax = 150;

export const getBoardList200ResponseDataItemRecommendedProductsItemReviewCountDefault = 0;
export const getBoardList200ResponseDataItemRecommendedProductsItemPriceUnitMax = 10;

export const getBoardList200ResponseDataItemRecommendedProductsItemUnitMax = 100;

export const getBoardList200ResponseDataItemRecommendedProductsItemKeywordMax = 500;

export const getBoardList200ResponseDataItemRecommendedProductsItemParentCategoryNameMax = 100;

export const getBoardList200ResponseDataItemRecommendedProductsItemReviewSummaryEnMax = 500;

export const getBoardList200ResponseDataItemRecommendedProductsItemReviewSummaryJaMax = 500;

export const getBoardList200ResponseDataItemBoardNameThMax = 100;

export const getBoardList200ResponseDataItemBoardNameEnMax = 100;

export const getBoardList200ResponseDataItemBoardNameJaMax = 100;

export const getBoardList200ResponseDataItemBoardDescriptionThMax = 255;

export const getBoardList200ResponseDataItemBoardDescriptionEnMax = 255;

export const getBoardList200ResponseDataItemBoardDescriptionJaMax = 255;

export const getBoardList200ResponseDataItemBoardFixedTitleMax = 500;

export const getBoardList200ResponseDataItemUserDisplayNameMax = 50;

export const getBoardList200ResponseDataItemUserFirstNameMax = 100;

export const getBoardList200ResponseDataItemUserLastNameMax = 100;

export const getBoardList200ResponseDataItemUserNickNameMax = 50;

export const getBoardList200ResponseDataItemUserPreferredPriceRangeMax = 50;

export const getBoardList200ResponseDataItemUserSocialInstagramMax = 100;

export const getBoardList200ResponseDataItemUserSocialFacebookMax = 100;

export const getBoardList200ResponseDataItemUserSocialXMax = 100;

export const getBoardList200ResponseDataItemUserSocialTiktokMax = 100;

export const getBoardList200ResponseDataItemUserSocialYoutubeMax = 100;

export const getBoardList200ResponseDataItemUserSocialWebsiteMax = 500;

export const getBoardList200ResponseDataItemUserAvatarMax = 255;

export const GetBoardList200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        data: zod
            .array(
                zod
                    .object({
                        post_id: zod.number().optional().describe("Numeric primary key (DB PK)"),
                        board_id: zod
                            .enum([
                                "review",
                                "tiktoks",
                                "beauty_news",
                                "editorial_reviews",
                                "expert_reviews",
                                "activities",
                                "test_modal",
                                "test_modeless",
                                "guidance",
                            ])
                            .optional()
                            .describe("Unique string identifier for the board (e.g., 'xxxboard')"),
                        user_id: zod.number().optional(),
                        post_title: zod.string().max(getBoardList200ResponseDataItemPostTitleMax).optional(),
                        content: zod.string().optional(),
                        post_product_id: zod.number().nullish().describe("Product ID for review posts"),
                        product_brand_name: zod.string().nullish().describe("Product brand name (flattened)"),
                        product_category_id: zod.number().nullish().describe("Product category ID (flattened)"),
                        product_category_name: zod.string().nullish().describe("Product category name (flattened)"),
                        product_parent_category_id: zod
                            .number()
                            .nullish()
                            .describe("Parent category ID (derived from product category)"),
                        product_parent_category_name: zod
                            .string()
                            .nullish()
                            .describe("Parent category name (flattened)"),
                        recommend_rank: zod
                            .number()
                            .min(1)
                            .max(getBoardList200ResponseDataItemRecommendRankMax)
                            .nullish()
                            .describe("Recommend rank for crown display (ISS:04-047)"),
                        monthly_rank: zod
                            .number()
                            .min(1)
                            .nullish()
                            .describe(
                                "Monthly ranking rank (materialized; derived from product monthly_rank for review posts) (ISS:04-048)",
                            ),
                        product_review_count: zod
                            .number()
                            .optional()
                            .describe("Total review count for this product (cached from product data)"),
                        product_rating_average: zod
                            .number()
                            .optional()
                            .describe("Average rating for this product (cached from product data)"),
                        user_rating_average: zod
                            .number()
                            .optional()
                            .describe("Current user's average rating calculated from their review answers"),
                        attached_images: zod
                            .array(zod.string())
                            .optional()
                            .describe("Images uploaded via button\/form"),
                        attached_videos: zod
                            .array(zod.string())
                            .optional()
                            .describe("Videos uploaded via button\/form"),
                        content_images: zod
                            .array(zod.string())
                            .optional()
                            .describe("Image filenames extracted from content (rich text embedded images)"),
                        sns_shares: zod
                            .array(zod.enum(["instagram", "tiktok"]))
                            .optional()
                            .describe("SNS share destinations requested for the post."),
                        sns_links: zod
                            .array(zod.string().url())
                            .optional()
                            .describe("SNS platform links (Facebook, YouTube, etc.) stored as structured data"),
                        likes_count: zod.number().nullish().describe("Total number of likes"),
                        likes: zod
                            .array(
                                zod
                                    .object({
                                        like_id: zod.number().optional().describe("Unique like identifier"),
                                        post_id: zod.number().optional(),
                                        comment_id: zod.number().nullish(),
                                        user_id: zod
                                            .number()
                                            .optional()
                                            .describe("User nickname for display (nick_name field)"),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict(),
                            )
                            .optional()
                            .describe("Detailed likes with user info"),
                        reads_count: zod.number().nullish().describe("Total number of reads"),
                        reads: zod
                            .array(
                                zod
                                    .object({
                                        read_id: zod.number().optional().describe("Unique read identifier"),
                                        user_id: zod.number().optional().describe("User ID who read the post"),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict(),
                            )
                            .optional()
                            .describe("Detailed reads with user info"),
                        comments_count: zod.number().nullish().describe("Total number of comments"),
                        comments: zod
                            .array(
                                zod
                                    .object({
                                        comment_id: zod.number().optional(),
                                        post_id: zod.number().optional(),
                                        board_id: zod
                                            .enum([
                                                "review",
                                                "tiktoks",
                                                "beauty_news",
                                                "editorial_reviews",
                                                "expert_reviews",
                                                "activities",
                                                "test_modal",
                                                "test_modeless",
                                                "guidance",
                                            ])
                                            .optional(),
                                        user_id: zod.number().optional(),
                                        content: zod.string().optional(),
                                        parent_comment_id: zod
                                            .number()
                                            .nullish()
                                            .describe(
                                                "Parent comment ID (null allowed in implementation for root comments)",
                                            ),
                                        replies_count: zod.number().optional(),
                                        like_count: zod
                                            .number()
                                            .optional()
                                            .describe("Deprecated - use likes_count instead"),
                                        likes_count: zod
                                            .number()
                                            .nullish()
                                            .describe("Total number of likes for this comment"),
                                        is_liked: zod
                                            .boolean()
                                            .optional()
                                            .describe("Whether current user has liked this comment"),
                                        likes: zod
                                            .array(
                                                zod
                                                    .object({
                                                        like_id: zod
                                                            .number()
                                                            .optional()
                                                            .describe("Unique like identifier"),
                                                        post_id: zod.number().optional(),
                                                        comment_id: zod.number().nullish(),
                                                        user_id: zod
                                                            .number()
                                                            .optional()
                                                            .describe("User nickname for display (nick_name field)"),
                                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                                    })
                                                    .strict(),
                                            )
                                            .optional()
                                            .describe("Detailed likes with user info"),
                                        replies: zod
                                            .array(zod.object({}).passthrough())
                                            .optional()
                                            .describe("Nested child comments for this comment"),
                                        delete_flag: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                        user: zod
                                            .object({
                                                user_id: zod.number().optional(),
                                                display_name: zod
                                                    .string()
                                                    .max(getBoardList200ResponseDataItemCommentsItemUserDisplayNameMax)
                                                    .optional()
                                                    .describe("Full display name"),
                                                first_name: zod
                                                    .string()
                                                    .max(getBoardList200ResponseDataItemCommentsItemUserFirstNameMax)
                                                    .optional()
                                                    .describe("First name"),
                                                last_name: zod
                                                    .string()
                                                    .max(getBoardList200ResponseDataItemCommentsItemUserLastNameMax)
                                                    .optional()
                                                    .describe("Last name"),
                                                nick_name: zod
                                                    .string()
                                                    .max(getBoardList200ResponseDataItemCommentsItemUserNickNameMax)
                                                    .optional()
                                                    .describe("Nickname or username"),
                                                date_of_birth: zod
                                                    .string()
                                                    .date()
                                                    .nullish()
                                                    .describe("Date of birth in YYYY-MM-DD format"),
                                                gender: zod
                                                    .enum(["male", "female", "other", "prefer_not_to_say"])
                                                    .nullish()
                                                    .describe("Gender identity (optional for public display)"),
                                                skin_type: zod
                                                    .enum([
                                                        "normal",
                                                        "oily",
                                                        "dry",
                                                        "combination",
                                                        "sensitive",
                                                        "dehydrated_oily",
                                                        "not_sure",
                                                    ])
                                                    .nullish()
                                                    .describe("Skin type (preference profile)"),
                                                skin_concerns: zod
                                                    .array(zod.string())
                                                    .optional()
                                                    .describe("Skin concerns (multiple selection)"),
                                                skin_tone: zod
                                                    .enum(["very_fair", "fair", "medium", "tan", "deep", "not_sure"])
                                                    .nullish(),
                                                hair_type: zod
                                                    .enum([
                                                        "straight_coarse",
                                                        "straight_soft",
                                                        "wavy_coarse",
                                                        "wavy_soft",
                                                        "normal_hair",
                                                        "not_sure",
                                                    ])
                                                    .nullish(),
                                                makeup_frequency: zod
                                                    .enum(["every_day", "occasionally", "rarely"])
                                                    .nullish(),
                                                hair_scalp_concerns: zod
                                                    .array(zod.string())
                                                    .optional()
                                                    .describe("Hair & scalp concerns (multiple selection)"),
                                                beauty_wellness_interests: zod
                                                    .array(zod.string())
                                                    .optional()
                                                    .describe("Beauty & wellness interests (multiple selection)"),
                                                lifestyle_environment: zod
                                                    .array(zod.string())
                                                    .optional()
                                                    .describe("Lifestyle environment (multiple selection)"),
                                                preferred_price_range: zod
                                                    .string()
                                                    .max(
                                                        getBoardList200ResponseDataItemCommentsItemUserPreferredPriceRangeMax,
                                                    )
                                                    .optional()
                                                    .describe("Preferred price range (single select)"),
                                                social_instagram: zod
                                                    .string()
                                                    .max(
                                                        getBoardList200ResponseDataItemCommentsItemUserSocialInstagramMax,
                                                    )
                                                    .optional(),
                                                social_facebook: zod
                                                    .string()
                                                    .max(
                                                        getBoardList200ResponseDataItemCommentsItemUserSocialFacebookMax,
                                                    )
                                                    .optional(),
                                                social_x: zod
                                                    .string()
                                                    .max(getBoardList200ResponseDataItemCommentsItemUserSocialXMax)
                                                    .optional(),
                                                social_tiktok: zod
                                                    .string()
                                                    .max(getBoardList200ResponseDataItemCommentsItemUserSocialTiktokMax)
                                                    .optional(),
                                                social_youtube: zod
                                                    .string()
                                                    .max(
                                                        getBoardList200ResponseDataItemCommentsItemUserSocialYoutubeMax,
                                                    )
                                                    .optional(),
                                                social_website: zod
                                                    .string()
                                                    .max(
                                                        getBoardList200ResponseDataItemCommentsItemUserSocialWebsiteMax,
                                                    )
                                                    .optional(),
                                                posts_count: zod.number().nullish(),
                                                review_count: zod.number().nullish(),
                                                followers_count: zod.number().optional(),
                                                followings_count: zod.number().optional(),
                                                recommend_rank: zod.number().nullish(),
                                                avatar: zod
                                                    .string()
                                                    .max(getBoardList200ResponseDataItemCommentsItemUserAvatarMax)
                                                    .optional()
                                                    .describe("Avatar image path"),
                                                role: zod
                                                    .enum(["admin", "editor", "moderator", "expert", "user"])
                                                    .optional()
                                                    .describe("User role"),
                                                created_at: zod.string().datetime({ offset: true }).optional(),
                                            })
                                            .strict()
                                            .optional()
                                            .describe("User information for comment author"),
                                    })
                                    .strict(),
                            )
                            .optional()
                            .describe("List of comments with user and like information"),
                        review_enabled: zod
                            .union([zod.literal(0), zod.literal(1)])
                            .optional()
                            .describe("Whether reviews are enabled for this board"),
                        review_config: zod
                            .union([
                                zod
                                    .object({
                                        review_config_id: zod.number().optional(),
                                        review_config_name: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemReviewConfigOneReviewConfigNameMax)
                                            .optional(),
                                        general_description: zod.string().optional(),
                                        is_active: zod.number().optional(),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict(),
                                zod.null(),
                            ])
                            .optional()
                            .describe("Review configuration for this board"),
                        review_questions: zod
                            .array(
                                zod
                                    .object({
                                        review_question_id: zod.number().optional(),
                                        review_config_id: zod.number().optional(),
                                        question_text_th: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemReviewQuestionsItemQuestionTextThMax)
                                            .optional(),
                                        question_text_en: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemReviewQuestionsItemQuestionTextEnMax)
                                            .optional(),
                                        question_text_ja: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemReviewQuestionsItemQuestionTextJaMax)
                                            .optional(),
                                        data_type: zod
                                            .enum(["rating", "single_select", "multi_select", "text"])
                                            .optional(),
                                        option_th: zod.array(zod.string()).optional(),
                                        option_en: zod.array(zod.string()).optional(),
                                        option_ja: zod.array(zod.string()).optional(),
                                        is_required: zod.number().optional(),
                                        is_active: zod.number().optional(),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict(),
                            )
                            .optional()
                            .describe("List of review questions for this board"),
                        review_data: zod
                            .union([
                                zod
                                    .object({
                                        review_answer_id: zod.number().optional(),
                                        review_config_id: zod.number().optional(),
                                        answers: zod
                                            .array(
                                                zod
                                                    .object({
                                                        review_question_id: zod.number().optional(),
                                                        answer_value: zod.string().optional(),
                                                        question_text_th: zod
                                                            .string()
                                                            .max(
                                                                getBoardList200ResponseDataItemReviewDataOneAnswersItemQuestionTextThMax,
                                                            )
                                                            .optional(),
                                                        question_text_en: zod
                                                            .string()
                                                            .max(
                                                                getBoardList200ResponseDataItemReviewDataOneAnswersItemQuestionTextEnMax,
                                                            )
                                                            .optional(),
                                                        question_text_ja: zod
                                                            .string()
                                                            .max(
                                                                getBoardList200ResponseDataItemReviewDataOneAnswersItemQuestionTextJaMax,
                                                            )
                                                            .optional(),
                                                        question_type: zod.string().optional(),
                                                        option_th: zod.array(zod.string()).optional(),
                                                        option_en: zod.array(zod.string()).optional(),
                                                        option_ja: zod.array(zod.string()).optional(),
                                                        answer_value_th: zod.string().optional(),
                                                        answer_value_en: zod.string().optional(),
                                                        answer_value_ja: zod.string().optional(),
                                                    })
                                                    .strict()
                                                    .describe(
                                                        "Question text\/options\/localized answer values are denormalized onto each answer for display convenience.",
                                                    ),
                                            )
                                            .optional(),
                                    })
                                    .strict()
                                    .describe("Current user's review data for this post (null if not reviewed)"),
                                zod.null(),
                            ])
                            .optional(),
                        reviews_count: zod.number().optional().describe("Total number of reviews for this post"),
                        average_rating: zod.number().nullish().describe("Average rating score (null if no ratings)"),
                        rating_distribution: zod
                            .object({
                                "1": zod.number().optional(),
                                "2": zod.number().optional(),
                                "3": zod.number().optional(),
                                "4": zod.number().optional(),
                                "5": zod.number().optional(),
                            })
                            .strict()
                            .optional()
                            .describe("Distribution of rating values"),
                        has_user_reviewed: zod
                            .boolean()
                            .optional()
                            .describe("Whether the current user has reviewed this post"),
                        ads: zod
                            .array(
                                zod
                                    .object({
                                        ad_id: zod.number().optional(),
                                        ad_type: zod
                                            .enum([
                                                "PLACE_HOLDER",
                                                "PRODUCT_LINKER",
                                                "PRODUCT_LINKER_OGP",
                                                "PRODUCT_CARD",
                                                "TOP_BANNER",
                                                "SECTION_BANNER",
                                                "USERS_BANNER",
                                                "USERS_BANNER_RECTANGLE",
                                                "AUTH_BANNER",
                                            ])
                                            .optional(),
                                        image_path: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemAdsItemImagePathMax)
                                            .optional(),
                                        ad_title: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemAdsItemAdTitleMax)
                                            .optional(),
                                        ad_body: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemAdsItemAdBodyMax)
                                            .optional(),
                                        link_url: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemAdsItemLinkUrlMax)
                                            .optional()
                                            .describe(
                                                'URL, or \"\" when unset (per project convention for absent string data — see CLAUDE.md).',
                                            ),
                                        is_internal_modal_link: zod
                                            .boolean()
                                            .optional()
                                            .describe(
                                                "True when link_url points at an internal post whose board is modal_usage=MODAL — the ad should open that board's in-page modal instead of a new tab (see AdService::resolveInternalModalLink()).",
                                            ),
                                        modal_board_id: zod
                                            .string()
                                            .nullish()
                                            .describe(
                                                "board_id to open in-modal when is_internal_modal_link is true, otherwise null.",
                                            ),
                                        modal_post_id: zod
                                            .number()
                                            .nullish()
                                            .describe(
                                                "post_id to open in-modal when is_internal_modal_link is true, otherwise null.",
                                            ),
                                        ad_product_id: zod.number().nullish(),
                                        display_rate_percent: zod
                                            .number()
                                            .nullish()
                                            .describe(
                                                "Display probability weight (0-100) among ads competing for the same target",
                                            ),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict()
                                    .describe("Ad item embedded in a post response (pre-selected by ad target type)"),
                            )
                            .optional()
                            .describe(
                                "Ads pre-selected for this post. PLACE_HOLDER type is included when the post has no media (text-only). PRODUCT_LINKER \/ PRODUCT_LINKER_OGP type is included when the post has no linked product.\n",
                            ),
                        product: zod
                            .union([
                                zod
                                    .object({
                                        product_id: zod.number().optional().describe("Unique product identifier"),
                                        product_name: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemProductOneProductNameMax)
                                            .optional()
                                            .describe("Product name"),
                                        product_status: zod
                                            .enum([
                                                "draft",
                                                "editing",
                                                "released",
                                                "duplicated",
                                                "rejected",
                                                "dummy",
                                                "brand",
                                            ])
                                            .optional()
                                            .describe("Product publication status"),
                                        brand_id: zod.number().nullish().describe("Brand ID (flattened)"),
                                        brand_name: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemProductOneBrandNameMax)
                                            .optional()
                                            .describe("Brand name (flattened)"),
                                        brand: zod
                                            .object({
                                                brand_id: zod.number().nullish(),
                                                brand_name: zod
                                                    .string()
                                                    .max(getBoardList200ResponseDataItemProductOneBrandBrandNameMax)
                                                    .optional(),
                                            })
                                            .strict()
                                            .nullish()
                                            .describe("Nested brand data"),
                                        category_id: zod.number().nullish().describe("Category ID (flattened)"),
                                        category_name: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemProductOneCategoryNameMax)
                                            .optional()
                                            .describe("Category name (flattened)"),
                                        category: zod
                                            .object({
                                                category_id: zod.number().nullish(),
                                                category_name: zod
                                                    .string()
                                                    .max(
                                                        getBoardList200ResponseDataItemProductOneCategoryCategoryNameMax,
                                                    )
                                                    .optional(),
                                                parent_category_id: zod.number().nullish(),
                                                parent_category_name: zod
                                                    .string()
                                                    .max(
                                                        getBoardList200ResponseDataItemProductOneCategoryParentCategoryNameMax,
                                                    )
                                                    .optional(),
                                            })
                                            .strict()
                                            .nullish()
                                            .describe("Nested category data"),
                                        image_url: zod.string().optional().describe("Product image URL"),
                                        image: zod.string().optional().describe("Product image filename"),
                                        image_path: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemProductOneImagePathMax)
                                            .optional()
                                            .describe("Product image path"),
                                        review_count: zod
                                            .number()
                                            .nullish()
                                            .default(getBoardList200ResponseDataItemProductOneReviewCountDefault)
                                            .describe("Total review count"),
                                        average_rating: zod.number().nullish().describe("Average rating score"),
                                        recommend_rank: zod.number().nullish().describe("Recommendation rank"),
                                        general_description: zod
                                            .string()
                                            .optional()
                                            .describe("General product description (post-product-linker preview text)"),
                                        product_note: zod.string().optional(),
                                        product_url: zod.string().optional(),
                                        price: zod.number().nullish(),
                                        price_unit: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemProductOnePriceUnitMax)
                                            .optional(),
                                        group_id: zod.union([zod.number(), zod.string()]).nullish(),
                                        unit: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemProductOneUnitMax)
                                            .optional(),
                                        how_to_use: zod.string().optional(),
                                        ingredients: zod.string().optional(),
                                        keyword: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemProductOneKeywordMax)
                                            .optional(),
                                        created_by: zod.number().nullish(),
                                        updated_by: zod.number().nullish(),
                                        monthly_rank: zod.number().nullish(),
                                        manual_score_offset: zod.number().nullish(),
                                        parent_category_id: zod.number().nullish(),
                                        parent_category_name: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemProductOneParentCategoryNameMax)
                                            .optional(),
                                        likes_count: zod.number().nullish(),
                                        comments_count: zod.number().nullish(),
                                        reads_count: zod.number().nullish(),
                                        review_count_guest: zod.number().nullish(),
                                        posts_count: zod.number().nullish(),
                                        posts_count_guest: zod.number().nullish(),
                                        positive_points_th: zod.array(zod.string()).optional(),
                                        negative_points_th: zod.array(zod.string()).optional(),
                                        positive_points_en: zod.array(zod.string()).optional(),
                                        negative_points_en: zod.array(zod.string()).optional(),
                                        positive_points_ja: zod.array(zod.string()).optional(),
                                        negative_points_ja: zod.array(zod.string()).optional(),
                                        review_summary_th: zod.string().optional(),
                                        review_summary_en: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemProductOneReviewSummaryEnMax)
                                            .optional(),
                                        review_summary_ja: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemProductOneReviewSummaryJaMax)
                                            .optional(),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict()
                                    .describe("Linked product data embedded in a post response (pre-fetched for SSR)."),
                                zod.null(),
                            ])
                            .optional()
                            .describe(
                                "Linked product data pre-fetched for SSR rendering of the product linker. Null when the post has no linked product.\n",
                            ),
                        recommended_products: zod
                            .array(
                                zod
                                    .object({
                                        product_id: zod.number().optional().describe("Unique product identifier"),
                                        product_name: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemRecommendedProductsItemProductNameMax)
                                            .optional()
                                            .describe("Product name"),
                                        product_status: zod
                                            .enum([
                                                "draft",
                                                "editing",
                                                "released",
                                                "duplicated",
                                                "rejected",
                                                "dummy",
                                                "brand",
                                            ])
                                            .optional()
                                            .describe("Product publication status"),
                                        brand_id: zod.number().nullish().describe("Brand ID (flattened)"),
                                        brand_name: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemRecommendedProductsItemBrandNameMax)
                                            .optional()
                                            .describe("Brand name (flattened)"),
                                        brand: zod
                                            .object({
                                                brand_id: zod.number().nullish(),
                                                brand_name: zod
                                                    .string()
                                                    .max(
                                                        getBoardList200ResponseDataItemRecommendedProductsItemBrandBrandNameMax,
                                                    )
                                                    .optional(),
                                            })
                                            .strict()
                                            .nullish()
                                            .describe("Nested brand data"),
                                        category_id: zod.number().nullish().describe("Category ID (flattened)"),
                                        category_name: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemRecommendedProductsItemCategoryNameMax)
                                            .optional()
                                            .describe("Category name (flattened)"),
                                        category: zod
                                            .object({
                                                category_id: zod.number().nullish(),
                                                category_name: zod
                                                    .string()
                                                    .max(
                                                        getBoardList200ResponseDataItemRecommendedProductsItemCategoryCategoryNameMax,
                                                    )
                                                    .optional(),
                                                parent_category_id: zod.number().nullish(),
                                                parent_category_name: zod
                                                    .string()
                                                    .max(
                                                        getBoardList200ResponseDataItemRecommendedProductsItemCategoryParentCategoryNameMax,
                                                    )
                                                    .optional(),
                                            })
                                            .strict()
                                            .nullish()
                                            .describe("Nested category data"),
                                        image_url: zod.string().optional().describe("Product image URL"),
                                        image: zod.string().optional().describe("Product image filename"),
                                        image_path: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemRecommendedProductsItemImagePathMax)
                                            .optional()
                                            .describe("Product image path"),
                                        review_count: zod
                                            .number()
                                            .nullish()
                                            .default(
                                                getBoardList200ResponseDataItemRecommendedProductsItemReviewCountDefault,
                                            )
                                            .describe("Total review count"),
                                        average_rating: zod.number().nullish().describe("Average rating score"),
                                        recommend_rank: zod.number().nullish().describe("Recommendation rank"),
                                        general_description: zod
                                            .string()
                                            .optional()
                                            .describe("General product description (post-product-linker preview text)"),
                                        product_note: zod.string().optional(),
                                        product_url: zod.string().optional(),
                                        price: zod.number().nullish(),
                                        price_unit: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemRecommendedProductsItemPriceUnitMax)
                                            .optional(),
                                        group_id: zod.union([zod.number(), zod.string()]).nullish(),
                                        unit: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemRecommendedProductsItemUnitMax)
                                            .optional(),
                                        how_to_use: zod.string().optional(),
                                        ingredients: zod.string().optional(),
                                        keyword: zod
                                            .string()
                                            .max(getBoardList200ResponseDataItemRecommendedProductsItemKeywordMax)
                                            .optional(),
                                        created_by: zod.number().nullish(),
                                        updated_by: zod.number().nullish(),
                                        monthly_rank: zod.number().nullish(),
                                        manual_score_offset: zod.number().nullish(),
                                        parent_category_id: zod.number().nullish(),
                                        parent_category_name: zod
                                            .string()
                                            .max(
                                                getBoardList200ResponseDataItemRecommendedProductsItemParentCategoryNameMax,
                                            )
                                            .optional(),
                                        likes_count: zod.number().nullish(),
                                        comments_count: zod.number().nullish(),
                                        reads_count: zod.number().nullish(),
                                        review_count_guest: zod.number().nullish(),
                                        posts_count: zod.number().nullish(),
                                        posts_count_guest: zod.number().nullish(),
                                        positive_points_th: zod.array(zod.string()).optional(),
                                        negative_points_th: zod.array(zod.string()).optional(),
                                        positive_points_en: zod.array(zod.string()).optional(),
                                        negative_points_en: zod.array(zod.string()).optional(),
                                        positive_points_ja: zod.array(zod.string()).optional(),
                                        negative_points_ja: zod.array(zod.string()).optional(),
                                        review_summary_th: zod.string().optional(),
                                        review_summary_en: zod
                                            .string()
                                            .max(
                                                getBoardList200ResponseDataItemRecommendedProductsItemReviewSummaryEnMax,
                                            )
                                            .optional(),
                                        review_summary_ja: zod
                                            .string()
                                            .max(
                                                getBoardList200ResponseDataItemRecommendedProductsItemReviewSummaryJaMax,
                                            )
                                            .optional(),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict()
                                    .describe("Linked product data embedded in a post response (pre-fetched for SSR)."),
                            )
                            .optional()
                            .describe(
                                "Products recommended below the post's content: other products in the linked product's category, falling back to the same Trending Products selection shown on products\/ when the post has no linked product or its category has no other products. Empty array when none available.\n",
                            ),
                        delete_flag: zod
                            .union([zod.literal(0), zod.literal(1)])
                            .optional()
                            .describe("1 if deleted (soft delete), 0 otherwise"),
                        post_status: zod
                            .enum(["draft", "editing", "released", "rejected"])
                            .optional()
                            .describe(
                                "Post visibility status: released = publicly visible, hidden = visible to admin\/editor only",
                            ),
                        created_at: zod.string().datetime({ offset: true }).optional(),
                        updated_at: zod.string().datetime({ offset: true }).optional(),
                        board: zod
                            .object({
                                board_config_id: zod.number().optional(),
                                board_id: zod
                                    .enum([
                                        "review",
                                        "tiktoks",
                                        "beauty_news",
                                        "editorial_reviews",
                                        "expert_reviews",
                                        "activities",
                                        "test_modal",
                                        "test_modeless",
                                        "guidance",
                                    ])
                                    .optional(),
                                is_active: zod.number().optional(),
                                allowed_read_roles: zod.array(zod.string()).optional(),
                                allowed_write_roles: zod.array(zod.string()).optional(),
                                allowed_like_roles: zod.array(zod.string()).optional(),
                                allowed_comment_roles: zod.array(zod.string()).optional(),
                                comment_enabled: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                                board_type: zod.enum(["general", "news", "article", "review"]).optional(),
                                name_th: zod.string().max(getBoardList200ResponseDataItemBoardNameThMax).optional(),
                                name_en: zod.string().max(getBoardList200ResponseDataItemBoardNameEnMax).optional(),
                                name_ja: zod.string().max(getBoardList200ResponseDataItemBoardNameJaMax).optional(),
                                description_th: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemBoardDescriptionThMax)
                                    .optional(),
                                description_en: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemBoardDescriptionEnMax)
                                    .optional(),
                                description_ja: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemBoardDescriptionJaMax)
                                    .optional(),
                                list_style: zod
                                    .enum(["CARD", "LIST", "ONE_IMAGE_CARD", "TILES"])
                                    .optional()
                                    .describe(
                                        "Board list presentation style. LIST is reserved for future implementation.",
                                    ),
                                modal_usage: zod
                                    .enum(["MODELESS", "MODELESS_TARGET_BLANK", "MODAL", "DIRECT_MAXIMIZE"])
                                    .optional()
                                    .describe(
                                        "Post details display method: MODELESS = same-tab page navigation, MODELESS_TARGET_BLANK = new-tab navigation, MODAL = in-page modal",
                                    ),
                                modal_size: zod.enum(["N/A", "small", "medium", "large", "extra-large"]).optional(),
                                title_enabled: zod
                                    .union([zod.literal(0), zod.literal(1)])
                                    .optional()
                                    .describe("Post title input visibility: 1 = enabled, 0 = hidden\/fixed"),
                                fixed_title: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemBoardFixedTitleMax)
                                    .optional()
                                    .describe("Fixed title value used when title input is disabled"),
                                product_linker_show: zod
                                    .enum(["SHOW", "HIDE"])
                                    .optional()
                                    .describe(
                                        "Product linker section visibility in post form: SHOW = visible, HIDE = hidden",
                                    ),
                                target_product_show: zod
                                    .enum(["SHOW", "HIDE"])
                                    .optional()
                                    .describe(
                                        "Target Product preview visibility on post detail page: SHOW = visible, HIDE = hidden",
                                    ),
                                ogp_preview_show: zod
                                    .enum(["SHOW", "HIDE"])
                                    .optional()
                                    .describe(
                                        "SNS link OGP preview visibility on post detail page: SHOW = visible, HIDE = hidden",
                                    ),
                                user_info_show: zod
                                    .enum(["SHOW", "HIDE"])
                                    .optional()
                                    .describe(
                                        "User info section visibility on post card and detail: SHOW = visible, HIDE = hidden",
                                    ),
                                date_show: zod
                                    .enum(["SHOW", "HIDE"])
                                    .nullish()
                                    .describe(
                                        "Post date visibility in the top-right corner of the post detail view: SHOW = visible, HIDE = hidden",
                                    ),
                                show_content: zod.enum(["SHOW", "HIDE"]).optional(),
                                rating_overlay_show: zod.enum(["SHOW", "HIDE"]).optional(),
                                review_enabled: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                                review_config_id: zod.number().nullish(),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                                updated_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict()
                            .optional()
                            .describe("Board configuration and attributes"),
                        user: zod
                            .object({
                                user_id: zod.number().optional(),
                                display_name: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserDisplayNameMax)
                                    .optional()
                                    .describe("Full display name"),
                                first_name: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserFirstNameMax)
                                    .optional()
                                    .describe("First name"),
                                last_name: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserLastNameMax)
                                    .optional()
                                    .describe("Last name"),
                                nick_name: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserNickNameMax)
                                    .optional()
                                    .describe("Nickname or username"),
                                date_of_birth: zod
                                    .string()
                                    .date()
                                    .nullish()
                                    .describe("Date of birth in YYYY-MM-DD format"),
                                gender: zod
                                    .enum(["male", "female", "other", "prefer_not_to_say"])
                                    .nullish()
                                    .describe("Gender identity (optional for public display)"),
                                skin_type: zod
                                    .enum([
                                        "normal",
                                        "oily",
                                        "dry",
                                        "combination",
                                        "sensitive",
                                        "dehydrated_oily",
                                        "not_sure",
                                    ])
                                    .nullish()
                                    .describe("Skin type (preference profile)"),
                                skin_concerns: zod
                                    .array(zod.string())
                                    .optional()
                                    .describe("Skin concerns (multiple selection)"),
                                skin_tone: zod
                                    .enum(["very_fair", "fair", "medium", "tan", "deep", "not_sure"])
                                    .nullish(),
                                hair_type: zod
                                    .enum([
                                        "straight_coarse",
                                        "straight_soft",
                                        "wavy_coarse",
                                        "wavy_soft",
                                        "normal_hair",
                                        "not_sure",
                                    ])
                                    .nullish(),
                                makeup_frequency: zod.enum(["every_day", "occasionally", "rarely"]).nullish(),
                                hair_scalp_concerns: zod
                                    .array(zod.string())
                                    .optional()
                                    .describe("Hair & scalp concerns (multiple selection)"),
                                beauty_wellness_interests: zod
                                    .array(zod.string())
                                    .optional()
                                    .describe("Beauty & wellness interests (multiple selection)"),
                                lifestyle_environment: zod
                                    .array(zod.string())
                                    .optional()
                                    .describe("Lifestyle environment (multiple selection)"),
                                preferred_price_range: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserPreferredPriceRangeMax)
                                    .optional()
                                    .describe("Preferred price range (single select)"),
                                social_instagram: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserSocialInstagramMax)
                                    .optional(),
                                social_facebook: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserSocialFacebookMax)
                                    .optional(),
                                social_x: zod.string().max(getBoardList200ResponseDataItemUserSocialXMax).optional(),
                                social_tiktok: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserSocialTiktokMax)
                                    .optional(),
                                social_youtube: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserSocialYoutubeMax)
                                    .optional(),
                                social_website: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserSocialWebsiteMax)
                                    .optional(),
                                posts_count: zod.number().nullish(),
                                review_count: zod.number().nullish(),
                                followers_count: zod.number().optional(),
                                followings_count: zod.number().optional(),
                                recommend_rank: zod.number().nullish(),
                                avatar: zod
                                    .string()
                                    .max(getBoardList200ResponseDataItemUserAvatarMax)
                                    .optional()
                                    .describe("Avatar image path"),
                                role: zod
                                    .enum(["admin", "editor", "moderator", "expert", "user"])
                                    .optional()
                                    .describe("User role"),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict()
                            .optional()
                            .describe("User information (sanitized for public display)"),
                        ogp_image_map: zod
                            .record(zod.string(), zod.string())
                            .optional()
                            .describe("Map of SNS URL to cached OGP image URL (populated on update response)"),
                    })
                    .strict(),
            )
            .optional(),
        pagination: zod
            .object({
                current_page: zod.number().optional(),
                total: zod.number().optional(),
                per_page: zod.number().optional(),
                last_page: zod.number().optional(),
            })
            .strict()
            .optional()
            .describe("Matches ApiControllerBase::jsonPaginated()'s actual response shape."),
    })
    .strict();

/**
 * Upload an attachment (image or video) to temporary storage. Returns attachment_id.
 * @summary Upload temporary attachment
 */
export const UploadAttachmentBody = zod
    .object({
        file: zod.instanceof(File),
    })
    .strict();

export const UploadAttachment201Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        attachment: zod
            .object({
                attachment_id: zod.number().optional(),
                user_id: zod.number().optional(),
                path: zod.string().optional().describe("Relative storage path or URL"),
                mime_type: zod.string().optional(),
                size: zod.number().optional(),
                created_at: zod.string().datetime({ offset: true }).optional(),
            })
            .strict()
            .optional(),
    })
    .strict();

/**
 * @summary Delete temporary attachment
 */
export const DeleteAttachmentParams = zod
    .object({
        attachment_id: zod.number(),
    })
    .strict();

export const DeleteAttachment200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
    })
    .strict();

/**
 * Get detailed information about a specific post including its comments
 * @summary Get post detail with comments
 */

export const GetPostDetailParams = zod
    .object({
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .describe("Board identifier"),
        post_id: zod.number().min(1).describe("Post identifier"),
    })
    .strict();

export const getPostDetail200ResponseDataItemOnePostTitleMax = 300;

export const getPostDetail200ResponseDataItemOneRecommendRankMax = 15;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserDisplayNameMax = 50;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserFirstNameMax = 100;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserLastNameMax = 100;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserNickNameMax = 50;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserPreferredPriceRangeMax = 50;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserSocialInstagramMax = 100;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserSocialFacebookMax = 100;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserSocialXMax = 100;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserSocialTiktokMax = 100;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserSocialYoutubeMax = 100;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserSocialWebsiteMax = 500;

export const getPostDetail200ResponseDataItemTwoCommentsItemUserAvatarMax = 255;

export const getPostDetail200ResponseDataItemOneReviewConfigOneReviewConfigNameMax = 70;

export const getPostDetail200ResponseDataItemOneReviewQuestionsItemQuestionTextThMax = 200;

export const getPostDetail200ResponseDataItemOneReviewQuestionsItemQuestionTextEnMax = 200;

export const getPostDetail200ResponseDataItemOneReviewQuestionsItemQuestionTextJaMax = 200;

export const getPostDetail200ResponseDataItemOneReviewDataOneAnswersItemQuestionTextThMax = 200;

export const getPostDetail200ResponseDataItemOneReviewDataOneAnswersItemQuestionTextEnMax = 200;

export const getPostDetail200ResponseDataItemOneReviewDataOneAnswersItemQuestionTextJaMax = 200;

export const getPostDetail200ResponseDataItemOneAdsItemImagePathMax = 150;

export const getPostDetail200ResponseDataItemOneAdsItemAdTitleMax = 40;

export const getPostDetail200ResponseDataItemOneAdsItemAdBodyMax = 130;

export const getPostDetail200ResponseDataItemOneAdsItemLinkUrlMax = 500;

export const getPostDetail200ResponseDataItemOneProductOneProductNameMax = 255;

export const getPostDetail200ResponseDataItemOneProductOneBrandNameMax = 100;

export const getPostDetail200ResponseDataItemOneProductOneBrandBrandNameMax = 100;

export const getPostDetail200ResponseDataItemOneProductOneCategoryNameMax = 100;

export const getPostDetail200ResponseDataItemOneProductOneCategoryCategoryNameMax = 100;

export const getPostDetail200ResponseDataItemOneProductOneCategoryParentCategoryNameMax = 100;

export const getPostDetail200ResponseDataItemOneProductOneImagePathMax = 150;

export const getPostDetail200ResponseDataItemOneProductOneReviewCountDefault = 0;
export const getPostDetail200ResponseDataItemOneProductOnePriceUnitMax = 10;

export const getPostDetail200ResponseDataItemOneProductOneUnitMax = 100;

export const getPostDetail200ResponseDataItemOneProductOneKeywordMax = 500;

export const getPostDetail200ResponseDataItemOneProductOneParentCategoryNameMax = 100;

export const getPostDetail200ResponseDataItemOneProductOneReviewSummaryEnMax = 500;

export const getPostDetail200ResponseDataItemOneProductOneReviewSummaryJaMax = 500;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemProductNameMax = 255;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemBrandNameMax = 100;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemBrandBrandNameMax = 100;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemCategoryNameMax = 100;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemCategoryCategoryNameMax = 100;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemCategoryParentCategoryNameMax = 100;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemImagePathMax = 150;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemReviewCountDefault = 0;
export const getPostDetail200ResponseDataItemOneRecommendedProductsItemPriceUnitMax = 10;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemUnitMax = 100;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemKeywordMax = 500;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemParentCategoryNameMax = 100;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemReviewSummaryEnMax = 500;

export const getPostDetail200ResponseDataItemOneRecommendedProductsItemReviewSummaryJaMax = 500;

export const getPostDetail200ResponseDataItemOneBoardNameThMax = 100;

export const getPostDetail200ResponseDataItemOneBoardNameEnMax = 100;

export const getPostDetail200ResponseDataItemOneBoardNameJaMax = 100;

export const getPostDetail200ResponseDataItemOneBoardDescriptionThMax = 255;

export const getPostDetail200ResponseDataItemOneBoardDescriptionEnMax = 255;

export const getPostDetail200ResponseDataItemOneBoardDescriptionJaMax = 255;

export const getPostDetail200ResponseDataItemOneBoardFixedTitleMax = 500;

export const getPostDetail200ResponseDataItemOneUserDisplayNameMax = 50;

export const getPostDetail200ResponseDataItemOneUserFirstNameMax = 100;

export const getPostDetail200ResponseDataItemOneUserLastNameMax = 100;

export const getPostDetail200ResponseDataItemOneUserNickNameMax = 50;

export const getPostDetail200ResponseDataItemOneUserPreferredPriceRangeMax = 50;

export const getPostDetail200ResponseDataItemOneUserSocialInstagramMax = 100;

export const getPostDetail200ResponseDataItemOneUserSocialFacebookMax = 100;

export const getPostDetail200ResponseDataItemOneUserSocialXMax = 100;

export const getPostDetail200ResponseDataItemOneUserSocialTiktokMax = 100;

export const getPostDetail200ResponseDataItemOneUserSocialYoutubeMax = 100;

export const getPostDetail200ResponseDataItemOneUserSocialWebsiteMax = 500;

export const getPostDetail200ResponseDataItemOneUserAvatarMax = 255;

export const GetPostDetail200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        message: zod.string().optional(),
        data: zod
            .object({
                item: zod
                    .object({
                        post_id: zod.number().optional().describe("Numeric primary key (DB PK)"),
                        board_id: zod
                            .enum([
                                "review",
                                "tiktoks",
                                "beauty_news",
                                "editorial_reviews",
                                "expert_reviews",
                                "activities",
                                "test_modal",
                                "test_modeless",
                                "guidance",
                            ])
                            .optional()
                            .describe("Unique string identifier for the board (e.g., 'xxxboard')"),
                        user_id: zod.number().optional(),
                        post_title: zod.string().max(getPostDetail200ResponseDataItemOnePostTitleMax).optional(),
                        content: zod.string().optional(),
                        post_product_id: zod.number().nullish().describe("Product ID for review posts"),
                        product_brand_name: zod.string().nullish().describe("Product brand name (flattened)"),
                        product_category_id: zod.number().nullish().describe("Product category ID (flattened)"),
                        product_category_name: zod.string().nullish().describe("Product category name (flattened)"),
                        product_parent_category_id: zod
                            .number()
                            .nullish()
                            .describe("Parent category ID (derived from product category)"),
                        product_parent_category_name: zod
                            .string()
                            .nullish()
                            .describe("Parent category name (flattened)"),
                        recommend_rank: zod
                            .number()
                            .min(1)
                            .max(getPostDetail200ResponseDataItemOneRecommendRankMax)
                            .nullish()
                            .describe("Recommend rank for crown display (ISS:04-047)"),
                        monthly_rank: zod
                            .number()
                            .min(1)
                            .nullish()
                            .describe(
                                "Monthly ranking rank (materialized; derived from product monthly_rank for review posts) (ISS:04-048)",
                            ),
                        product_review_count: zod
                            .number()
                            .optional()
                            .describe("Total review count for this product (cached from product data)"),
                        product_rating_average: zod
                            .number()
                            .optional()
                            .describe("Average rating for this product (cached from product data)"),
                        user_rating_average: zod
                            .number()
                            .optional()
                            .describe("Current user's average rating calculated from their review answers"),
                        attached_images: zod
                            .array(zod.string())
                            .optional()
                            .describe("Images uploaded via button\/form"),
                        attached_videos: zod
                            .array(zod.string())
                            .optional()
                            .describe("Videos uploaded via button\/form"),
                        content_images: zod
                            .array(zod.string())
                            .optional()
                            .describe("Image filenames extracted from content (rich text embedded images)"),
                        sns_shares: zod
                            .array(zod.enum(["instagram", "tiktok"]))
                            .optional()
                            .describe("SNS share destinations requested for the post."),
                        sns_links: zod
                            .array(zod.string().url())
                            .optional()
                            .describe("SNS platform links (Facebook, YouTube, etc.) stored as structured data"),
                        likes_count: zod.number().nullish().describe("Total number of likes"),
                        likes: zod
                            .array(
                                zod
                                    .object({
                                        like_id: zod.number().optional().describe("Unique like identifier"),
                                        post_id: zod.number().optional(),
                                        comment_id: zod.number().nullish(),
                                        user_id: zod
                                            .number()
                                            .optional()
                                            .describe("User nickname for display (nick_name field)"),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict(),
                            )
                            .optional()
                            .describe("Detailed likes with user info"),
                        reads_count: zod.number().nullish().describe("Total number of reads"),
                        reads: zod
                            .array(
                                zod
                                    .object({
                                        read_id: zod.number().optional().describe("Unique read identifier"),
                                        user_id: zod.number().optional().describe("User ID who read the post"),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict(),
                            )
                            .optional()
                            .describe("Detailed reads with user info"),
                        comments_count: zod.number().nullish().describe("Total number of comments"),
                        comments: zod
                            .array(
                                zod
                                    .object({
                                        comment_id: zod.number().optional(),
                                        post_id: zod.number().optional(),
                                        board_id: zod
                                            .enum([
                                                "review",
                                                "tiktoks",
                                                "beauty_news",
                                                "editorial_reviews",
                                                "expert_reviews",
                                                "activities",
                                                "test_modal",
                                                "test_modeless",
                                                "guidance",
                                            ])
                                            .optional(),
                                        user_id: zod.number().optional(),
                                        content: zod.string().optional(),
                                        parent_comment_id: zod
                                            .number()
                                            .nullish()
                                            .describe(
                                                "Parent comment ID (null allowed in implementation for root comments)",
                                            ),
                                        replies_count: zod.number().optional(),
                                        like_count: zod
                                            .number()
                                            .optional()
                                            .describe("Deprecated - use likes_count instead"),
                                        likes_count: zod
                                            .number()
                                            .nullish()
                                            .describe("Total number of likes for this comment"),
                                        is_liked: zod
                                            .boolean()
                                            .optional()
                                            .describe("Whether current user has liked this comment"),
                                        likes: zod
                                            .array(
                                                zod
                                                    .object({
                                                        like_id: zod
                                                            .number()
                                                            .optional()
                                                            .describe("Unique like identifier"),
                                                        post_id: zod.number().optional(),
                                                        comment_id: zod.number().nullish(),
                                                        user_id: zod
                                                            .number()
                                                            .optional()
                                                            .describe("User nickname for display (nick_name field)"),
                                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                                    })
                                                    .strict(),
                                            )
                                            .optional()
                                            .describe("Detailed likes with user info"),
                                        replies: zod
                                            .array(zod.object({}).passthrough())
                                            .optional()
                                            .describe("Nested child comments for this comment"),
                                        delete_flag: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                        user: zod
                                            .object({
                                                user_id: zod.number().optional(),
                                                display_name: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemTwoCommentsItemUserDisplayNameMax,
                                                    )
                                                    .optional()
                                                    .describe("Full display name"),
                                                first_name: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemTwoCommentsItemUserFirstNameMax,
                                                    )
                                                    .optional()
                                                    .describe("First name"),
                                                last_name: zod
                                                    .string()
                                                    .max(getPostDetail200ResponseDataItemTwoCommentsItemUserLastNameMax)
                                                    .optional()
                                                    .describe("Last name"),
                                                nick_name: zod
                                                    .string()
                                                    .max(getPostDetail200ResponseDataItemTwoCommentsItemUserNickNameMax)
                                                    .optional()
                                                    .describe("Nickname or username"),
                                                date_of_birth: zod
                                                    .string()
                                                    .date()
                                                    .nullish()
                                                    .describe("Date of birth in YYYY-MM-DD format"),
                                                gender: zod
                                                    .enum(["male", "female", "other", "prefer_not_to_say"])
                                                    .nullish()
                                                    .describe("Gender identity (optional for public display)"),
                                                skin_type: zod
                                                    .enum([
                                                        "normal",
                                                        "oily",
                                                        "dry",
                                                        "combination",
                                                        "sensitive",
                                                        "dehydrated_oily",
                                                        "not_sure",
                                                    ])
                                                    .nullish()
                                                    .describe("Skin type (preference profile)"),
                                                skin_concerns: zod
                                                    .array(zod.string())
                                                    .optional()
                                                    .describe("Skin concerns (multiple selection)"),
                                                skin_tone: zod
                                                    .enum(["very_fair", "fair", "medium", "tan", "deep", "not_sure"])
                                                    .nullish(),
                                                hair_type: zod
                                                    .enum([
                                                        "straight_coarse",
                                                        "straight_soft",
                                                        "wavy_coarse",
                                                        "wavy_soft",
                                                        "normal_hair",
                                                        "not_sure",
                                                    ])
                                                    .nullish(),
                                                makeup_frequency: zod
                                                    .enum(["every_day", "occasionally", "rarely"])
                                                    .nullish(),
                                                hair_scalp_concerns: zod
                                                    .array(zod.string())
                                                    .optional()
                                                    .describe("Hair & scalp concerns (multiple selection)"),
                                                beauty_wellness_interests: zod
                                                    .array(zod.string())
                                                    .optional()
                                                    .describe("Beauty & wellness interests (multiple selection)"),
                                                lifestyle_environment: zod
                                                    .array(zod.string())
                                                    .optional()
                                                    .describe("Lifestyle environment (multiple selection)"),
                                                preferred_price_range: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemTwoCommentsItemUserPreferredPriceRangeMax,
                                                    )
                                                    .optional()
                                                    .describe("Preferred price range (single select)"),
                                                social_instagram: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemTwoCommentsItemUserSocialInstagramMax,
                                                    )
                                                    .optional(),
                                                social_facebook: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemTwoCommentsItemUserSocialFacebookMax,
                                                    )
                                                    .optional(),
                                                social_x: zod
                                                    .string()
                                                    .max(getPostDetail200ResponseDataItemTwoCommentsItemUserSocialXMax)
                                                    .optional(),
                                                social_tiktok: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemTwoCommentsItemUserSocialTiktokMax,
                                                    )
                                                    .optional(),
                                                social_youtube: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemTwoCommentsItemUserSocialYoutubeMax,
                                                    )
                                                    .optional(),
                                                social_website: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemTwoCommentsItemUserSocialWebsiteMax,
                                                    )
                                                    .optional(),
                                                posts_count: zod.number().nullish(),
                                                review_count: zod.number().nullish(),
                                                followers_count: zod.number().optional(),
                                                followings_count: zod.number().optional(),
                                                recommend_rank: zod.number().nullish(),
                                                avatar: zod
                                                    .string()
                                                    .max(getPostDetail200ResponseDataItemTwoCommentsItemUserAvatarMax)
                                                    .optional()
                                                    .describe("Avatar image path"),
                                                role: zod
                                                    .enum(["admin", "editor", "moderator", "expert", "user"])
                                                    .optional()
                                                    .describe("User role"),
                                                created_at: zod.string().datetime({ offset: true }).optional(),
                                            })
                                            .strict()
                                            .optional()
                                            .describe("User information for comment author"),
                                    })
                                    .strict(),
                            )
                            .optional()
                            .describe("Comments for this post with user information"),
                        review_enabled: zod
                            .union([zod.literal(0), zod.literal(1)])
                            .optional()
                            .describe("Whether reviews are enabled for this board"),
                        review_config: zod
                            .union([
                                zod
                                    .object({
                                        review_config_id: zod.number().optional(),
                                        review_config_name: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneReviewConfigOneReviewConfigNameMax)
                                            .optional(),
                                        general_description: zod.string().optional(),
                                        is_active: zod.number().optional(),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict(),
                                zod.null(),
                            ])
                            .optional()
                            .describe("Review configuration for this board"),
                        review_questions: zod
                            .array(
                                zod
                                    .object({
                                        review_question_id: zod.number().optional(),
                                        review_config_id: zod.number().optional(),
                                        question_text_th: zod
                                            .string()
                                            .max(
                                                getPostDetail200ResponseDataItemOneReviewQuestionsItemQuestionTextThMax,
                                            )
                                            .optional(),
                                        question_text_en: zod
                                            .string()
                                            .max(
                                                getPostDetail200ResponseDataItemOneReviewQuestionsItemQuestionTextEnMax,
                                            )
                                            .optional(),
                                        question_text_ja: zod
                                            .string()
                                            .max(
                                                getPostDetail200ResponseDataItemOneReviewQuestionsItemQuestionTextJaMax,
                                            )
                                            .optional(),
                                        data_type: zod
                                            .enum(["rating", "single_select", "multi_select", "text"])
                                            .optional(),
                                        option_th: zod.array(zod.string()).optional(),
                                        option_en: zod.array(zod.string()).optional(),
                                        option_ja: zod.array(zod.string()).optional(),
                                        is_required: zod.number().optional(),
                                        is_active: zod.number().optional(),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict(),
                            )
                            .optional()
                            .describe("List of review questions for this board"),
                        review_data: zod
                            .union([
                                zod
                                    .object({
                                        review_answer_id: zod.number().optional(),
                                        review_config_id: zod.number().optional(),
                                        answers: zod
                                            .array(
                                                zod
                                                    .object({
                                                        review_question_id: zod.number().optional(),
                                                        answer_value: zod.string().optional(),
                                                        question_text_th: zod
                                                            .string()
                                                            .max(
                                                                getPostDetail200ResponseDataItemOneReviewDataOneAnswersItemQuestionTextThMax,
                                                            )
                                                            .optional(),
                                                        question_text_en: zod
                                                            .string()
                                                            .max(
                                                                getPostDetail200ResponseDataItemOneReviewDataOneAnswersItemQuestionTextEnMax,
                                                            )
                                                            .optional(),
                                                        question_text_ja: zod
                                                            .string()
                                                            .max(
                                                                getPostDetail200ResponseDataItemOneReviewDataOneAnswersItemQuestionTextJaMax,
                                                            )
                                                            .optional(),
                                                        question_type: zod.string().optional(),
                                                        option_th: zod.array(zod.string()).optional(),
                                                        option_en: zod.array(zod.string()).optional(),
                                                        option_ja: zod.array(zod.string()).optional(),
                                                        answer_value_th: zod.string().optional(),
                                                        answer_value_en: zod.string().optional(),
                                                        answer_value_ja: zod.string().optional(),
                                                    })
                                                    .strict()
                                                    .describe(
                                                        "Question text\/options\/localized answer values are denormalized onto each answer for display convenience.",
                                                    ),
                                            )
                                            .optional(),
                                    })
                                    .strict()
                                    .describe("Current user's review data for this post (null if not reviewed)"),
                                zod.null(),
                            ])
                            .optional(),
                        reviews_count: zod.number().optional().describe("Total number of reviews for this post"),
                        average_rating: zod.number().nullish().describe("Average rating score (null if no ratings)"),
                        rating_distribution: zod
                            .object({
                                "1": zod.number().optional(),
                                "2": zod.number().optional(),
                                "3": zod.number().optional(),
                                "4": zod.number().optional(),
                                "5": zod.number().optional(),
                            })
                            .strict()
                            .optional()
                            .describe("Distribution of rating values"),
                        has_user_reviewed: zod
                            .boolean()
                            .optional()
                            .describe("Whether the current user has reviewed this post"),
                        ads: zod
                            .array(
                                zod
                                    .object({
                                        ad_id: zod.number().optional(),
                                        ad_type: zod
                                            .enum([
                                                "PLACE_HOLDER",
                                                "PRODUCT_LINKER",
                                                "PRODUCT_LINKER_OGP",
                                                "PRODUCT_CARD",
                                                "TOP_BANNER",
                                                "SECTION_BANNER",
                                                "USERS_BANNER",
                                                "USERS_BANNER_RECTANGLE",
                                                "AUTH_BANNER",
                                            ])
                                            .optional(),
                                        image_path: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneAdsItemImagePathMax)
                                            .optional(),
                                        ad_title: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneAdsItemAdTitleMax)
                                            .optional(),
                                        ad_body: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneAdsItemAdBodyMax)
                                            .optional(),
                                        link_url: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneAdsItemLinkUrlMax)
                                            .optional()
                                            .describe(
                                                'URL, or \"\" when unset (per project convention for absent string data — see CLAUDE.md).',
                                            ),
                                        is_internal_modal_link: zod
                                            .boolean()
                                            .optional()
                                            .describe(
                                                "True when link_url points at an internal post whose board is modal_usage=MODAL — the ad should open that board's in-page modal instead of a new tab (see AdService::resolveInternalModalLink()).",
                                            ),
                                        modal_board_id: zod
                                            .string()
                                            .nullish()
                                            .describe(
                                                "board_id to open in-modal when is_internal_modal_link is true, otherwise null.",
                                            ),
                                        modal_post_id: zod
                                            .number()
                                            .nullish()
                                            .describe(
                                                "post_id to open in-modal when is_internal_modal_link is true, otherwise null.",
                                            ),
                                        ad_product_id: zod.number().nullish(),
                                        display_rate_percent: zod
                                            .number()
                                            .nullish()
                                            .describe(
                                                "Display probability weight (0-100) among ads competing for the same target",
                                            ),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict()
                                    .describe("Ad item embedded in a post response (pre-selected by ad target type)"),
                            )
                            .optional()
                            .describe(
                                "Ads pre-selected for this post. PLACE_HOLDER type is included when the post has no media (text-only). PRODUCT_LINKER \/ PRODUCT_LINKER_OGP type is included when the post has no linked product.\n",
                            ),
                        product: zod
                            .union([
                                zod
                                    .object({
                                        product_id: zod.number().optional().describe("Unique product identifier"),
                                        product_name: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneProductOneProductNameMax)
                                            .optional()
                                            .describe("Product name"),
                                        product_status: zod
                                            .enum([
                                                "draft",
                                                "editing",
                                                "released",
                                                "duplicated",
                                                "rejected",
                                                "dummy",
                                                "brand",
                                            ])
                                            .optional()
                                            .describe("Product publication status"),
                                        brand_id: zod.number().nullish().describe("Brand ID (flattened)"),
                                        brand_name: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneProductOneBrandNameMax)
                                            .optional()
                                            .describe("Brand name (flattened)"),
                                        brand: zod
                                            .object({
                                                brand_id: zod.number().nullish(),
                                                brand_name: zod
                                                    .string()
                                                    .max(getPostDetail200ResponseDataItemOneProductOneBrandBrandNameMax)
                                                    .optional(),
                                            })
                                            .strict()
                                            .nullish()
                                            .describe("Nested brand data"),
                                        category_id: zod.number().nullish().describe("Category ID (flattened)"),
                                        category_name: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneProductOneCategoryNameMax)
                                            .optional()
                                            .describe("Category name (flattened)"),
                                        category: zod
                                            .object({
                                                category_id: zod.number().nullish(),
                                                category_name: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemOneProductOneCategoryCategoryNameMax,
                                                    )
                                                    .optional(),
                                                parent_category_id: zod.number().nullish(),
                                                parent_category_name: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemOneProductOneCategoryParentCategoryNameMax,
                                                    )
                                                    .optional(),
                                            })
                                            .strict()
                                            .nullish()
                                            .describe("Nested category data"),
                                        image_url: zod.string().optional().describe("Product image URL"),
                                        image: zod.string().optional().describe("Product image filename"),
                                        image_path: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneProductOneImagePathMax)
                                            .optional()
                                            .describe("Product image path"),
                                        review_count: zod
                                            .number()
                                            .nullish()
                                            .default(getPostDetail200ResponseDataItemOneProductOneReviewCountDefault)
                                            .describe("Total review count"),
                                        average_rating: zod.number().nullish().describe("Average rating score"),
                                        recommend_rank: zod.number().nullish().describe("Recommendation rank"),
                                        general_description: zod
                                            .string()
                                            .optional()
                                            .describe("General product description (post-product-linker preview text)"),
                                        product_note: zod.string().optional(),
                                        product_url: zod.string().optional(),
                                        price: zod.number().nullish(),
                                        price_unit: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneProductOnePriceUnitMax)
                                            .optional(),
                                        group_id: zod.union([zod.number(), zod.string()]).nullish(),
                                        unit: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneProductOneUnitMax)
                                            .optional(),
                                        how_to_use: zod.string().optional(),
                                        ingredients: zod.string().optional(),
                                        keyword: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneProductOneKeywordMax)
                                            .optional(),
                                        created_by: zod.number().nullish(),
                                        updated_by: zod.number().nullish(),
                                        monthly_rank: zod.number().nullish(),
                                        manual_score_offset: zod.number().nullish(),
                                        parent_category_id: zod.number().nullish(),
                                        parent_category_name: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneProductOneParentCategoryNameMax)
                                            .optional(),
                                        likes_count: zod.number().nullish(),
                                        comments_count: zod.number().nullish(),
                                        reads_count: zod.number().nullish(),
                                        review_count_guest: zod.number().nullish(),
                                        posts_count: zod.number().nullish(),
                                        posts_count_guest: zod.number().nullish(),
                                        positive_points_th: zod.array(zod.string()).optional(),
                                        negative_points_th: zod.array(zod.string()).optional(),
                                        positive_points_en: zod.array(zod.string()).optional(),
                                        negative_points_en: zod.array(zod.string()).optional(),
                                        positive_points_ja: zod.array(zod.string()).optional(),
                                        negative_points_ja: zod.array(zod.string()).optional(),
                                        review_summary_th: zod.string().optional(),
                                        review_summary_en: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneProductOneReviewSummaryEnMax)
                                            .optional(),
                                        review_summary_ja: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneProductOneReviewSummaryJaMax)
                                            .optional(),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict()
                                    .describe("Linked product data embedded in a post response (pre-fetched for SSR)."),
                                zod.null(),
                            ])
                            .optional()
                            .describe(
                                "Linked product data pre-fetched for SSR rendering of the product linker. Null when the post has no linked product.\n",
                            ),
                        recommended_products: zod
                            .array(
                                zod
                                    .object({
                                        product_id: zod.number().optional().describe("Unique product identifier"),
                                        product_name: zod
                                            .string()
                                            .max(
                                                getPostDetail200ResponseDataItemOneRecommendedProductsItemProductNameMax,
                                            )
                                            .optional()
                                            .describe("Product name"),
                                        product_status: zod
                                            .enum([
                                                "draft",
                                                "editing",
                                                "released",
                                                "duplicated",
                                                "rejected",
                                                "dummy",
                                                "brand",
                                            ])
                                            .optional()
                                            .describe("Product publication status"),
                                        brand_id: zod.number().nullish().describe("Brand ID (flattened)"),
                                        brand_name: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneRecommendedProductsItemBrandNameMax)
                                            .optional()
                                            .describe("Brand name (flattened)"),
                                        brand: zod
                                            .object({
                                                brand_id: zod.number().nullish(),
                                                brand_name: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemOneRecommendedProductsItemBrandBrandNameMax,
                                                    )
                                                    .optional(),
                                            })
                                            .strict()
                                            .nullish()
                                            .describe("Nested brand data"),
                                        category_id: zod.number().nullish().describe("Category ID (flattened)"),
                                        category_name: zod
                                            .string()
                                            .max(
                                                getPostDetail200ResponseDataItemOneRecommendedProductsItemCategoryNameMax,
                                            )
                                            .optional()
                                            .describe("Category name (flattened)"),
                                        category: zod
                                            .object({
                                                category_id: zod.number().nullish(),
                                                category_name: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemOneRecommendedProductsItemCategoryCategoryNameMax,
                                                    )
                                                    .optional(),
                                                parent_category_id: zod.number().nullish(),
                                                parent_category_name: zod
                                                    .string()
                                                    .max(
                                                        getPostDetail200ResponseDataItemOneRecommendedProductsItemCategoryParentCategoryNameMax,
                                                    )
                                                    .optional(),
                                            })
                                            .strict()
                                            .nullish()
                                            .describe("Nested category data"),
                                        image_url: zod.string().optional().describe("Product image URL"),
                                        image: zod.string().optional().describe("Product image filename"),
                                        image_path: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneRecommendedProductsItemImagePathMax)
                                            .optional()
                                            .describe("Product image path"),
                                        review_count: zod
                                            .number()
                                            .nullish()
                                            .default(
                                                getPostDetail200ResponseDataItemOneRecommendedProductsItemReviewCountDefault,
                                            )
                                            .describe("Total review count"),
                                        average_rating: zod.number().nullish().describe("Average rating score"),
                                        recommend_rank: zod.number().nullish().describe("Recommendation rank"),
                                        general_description: zod
                                            .string()
                                            .optional()
                                            .describe("General product description (post-product-linker preview text)"),
                                        product_note: zod.string().optional(),
                                        product_url: zod.string().optional(),
                                        price: zod.number().nullish(),
                                        price_unit: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneRecommendedProductsItemPriceUnitMax)
                                            .optional(),
                                        group_id: zod.union([zod.number(), zod.string()]).nullish(),
                                        unit: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneRecommendedProductsItemUnitMax)
                                            .optional(),
                                        how_to_use: zod.string().optional(),
                                        ingredients: zod.string().optional(),
                                        keyword: zod
                                            .string()
                                            .max(getPostDetail200ResponseDataItemOneRecommendedProductsItemKeywordMax)
                                            .optional(),
                                        created_by: zod.number().nullish(),
                                        updated_by: zod.number().nullish(),
                                        monthly_rank: zod.number().nullish(),
                                        manual_score_offset: zod.number().nullish(),
                                        parent_category_id: zod.number().nullish(),
                                        parent_category_name: zod
                                            .string()
                                            .max(
                                                getPostDetail200ResponseDataItemOneRecommendedProductsItemParentCategoryNameMax,
                                            )
                                            .optional(),
                                        likes_count: zod.number().nullish(),
                                        comments_count: zod.number().nullish(),
                                        reads_count: zod.number().nullish(),
                                        review_count_guest: zod.number().nullish(),
                                        posts_count: zod.number().nullish(),
                                        posts_count_guest: zod.number().nullish(),
                                        positive_points_th: zod.array(zod.string()).optional(),
                                        negative_points_th: zod.array(zod.string()).optional(),
                                        positive_points_en: zod.array(zod.string()).optional(),
                                        negative_points_en: zod.array(zod.string()).optional(),
                                        positive_points_ja: zod.array(zod.string()).optional(),
                                        negative_points_ja: zod.array(zod.string()).optional(),
                                        review_summary_th: zod.string().optional(),
                                        review_summary_en: zod
                                            .string()
                                            .max(
                                                getPostDetail200ResponseDataItemOneRecommendedProductsItemReviewSummaryEnMax,
                                            )
                                            .optional(),
                                        review_summary_ja: zod
                                            .string()
                                            .max(
                                                getPostDetail200ResponseDataItemOneRecommendedProductsItemReviewSummaryJaMax,
                                            )
                                            .optional(),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                        updated_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict()
                                    .describe("Linked product data embedded in a post response (pre-fetched for SSR)."),
                            )
                            .optional()
                            .describe(
                                "Products recommended below the post's content: other products in the linked product's category, falling back to the same Trending Products selection shown on products\/ when the post has no linked product or its category has no other products. Empty array when none available.\n",
                            ),
                        delete_flag: zod
                            .union([zod.literal(0), zod.literal(1)])
                            .optional()
                            .describe("1 if deleted (soft delete), 0 otherwise"),
                        post_status: zod
                            .enum(["draft", "editing", "released", "rejected"])
                            .optional()
                            .describe(
                                "Post visibility status: released = publicly visible, hidden = visible to admin\/editor only",
                            ),
                        created_at: zod.string().datetime({ offset: true }).optional(),
                        updated_at: zod.string().datetime({ offset: true }).optional(),
                        board: zod
                            .object({
                                board_config_id: zod.number().optional(),
                                board_id: zod
                                    .enum([
                                        "review",
                                        "tiktoks",
                                        "beauty_news",
                                        "editorial_reviews",
                                        "expert_reviews",
                                        "activities",
                                        "test_modal",
                                        "test_modeless",
                                        "guidance",
                                    ])
                                    .optional(),
                                is_active: zod.number().optional(),
                                allowed_read_roles: zod.array(zod.string()).optional(),
                                allowed_write_roles: zod.array(zod.string()).optional(),
                                allowed_like_roles: zod.array(zod.string()).optional(),
                                allowed_comment_roles: zod.array(zod.string()).optional(),
                                comment_enabled: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                                board_type: zod.enum(["general", "news", "article", "review"]).optional(),
                                name_th: zod.string().max(getPostDetail200ResponseDataItemOneBoardNameThMax).optional(),
                                name_en: zod.string().max(getPostDetail200ResponseDataItemOneBoardNameEnMax).optional(),
                                name_ja: zod.string().max(getPostDetail200ResponseDataItemOneBoardNameJaMax).optional(),
                                description_th: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneBoardDescriptionThMax)
                                    .optional(),
                                description_en: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneBoardDescriptionEnMax)
                                    .optional(),
                                description_ja: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneBoardDescriptionJaMax)
                                    .optional(),
                                list_style: zod
                                    .enum(["CARD", "LIST", "ONE_IMAGE_CARD", "TILES"])
                                    .optional()
                                    .describe(
                                        "Board list presentation style. LIST is reserved for future implementation.",
                                    ),
                                modal_usage: zod
                                    .enum(["MODELESS", "MODELESS_TARGET_BLANK", "MODAL", "DIRECT_MAXIMIZE"])
                                    .optional()
                                    .describe(
                                        "Post details display method: MODELESS = same-tab page navigation, MODELESS_TARGET_BLANK = new-tab navigation, MODAL = in-page modal",
                                    ),
                                modal_size: zod.enum(["N/A", "small", "medium", "large", "extra-large"]).optional(),
                                title_enabled: zod
                                    .union([zod.literal(0), zod.literal(1)])
                                    .optional()
                                    .describe("Post title input visibility: 1 = enabled, 0 = hidden\/fixed"),
                                fixed_title: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneBoardFixedTitleMax)
                                    .optional()
                                    .describe("Fixed title value used when title input is disabled"),
                                product_linker_show: zod
                                    .enum(["SHOW", "HIDE"])
                                    .optional()
                                    .describe(
                                        "Product linker section visibility in post form: SHOW = visible, HIDE = hidden",
                                    ),
                                target_product_show: zod
                                    .enum(["SHOW", "HIDE"])
                                    .optional()
                                    .describe(
                                        "Target Product preview visibility on post detail page: SHOW = visible, HIDE = hidden",
                                    ),
                                ogp_preview_show: zod
                                    .enum(["SHOW", "HIDE"])
                                    .optional()
                                    .describe(
                                        "SNS link OGP preview visibility on post detail page: SHOW = visible, HIDE = hidden",
                                    ),
                                user_info_show: zod
                                    .enum(["SHOW", "HIDE"])
                                    .optional()
                                    .describe(
                                        "User info section visibility on post card and detail: SHOW = visible, HIDE = hidden",
                                    ),
                                date_show: zod
                                    .enum(["SHOW", "HIDE"])
                                    .nullish()
                                    .describe(
                                        "Post date visibility in the top-right corner of the post detail view: SHOW = visible, HIDE = hidden",
                                    ),
                                show_content: zod.enum(["SHOW", "HIDE"]).optional(),
                                rating_overlay_show: zod.enum(["SHOW", "HIDE"]).optional(),
                                review_enabled: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                                review_config_id: zod.number().nullish(),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                                updated_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict()
                            .optional()
                            .describe("Board configuration and attributes"),
                        user: zod
                            .object({
                                user_id: zod.number().optional(),
                                display_name: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserDisplayNameMax)
                                    .optional()
                                    .describe("Full display name"),
                                first_name: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserFirstNameMax)
                                    .optional()
                                    .describe("First name"),
                                last_name: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserLastNameMax)
                                    .optional()
                                    .describe("Last name"),
                                nick_name: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserNickNameMax)
                                    .optional()
                                    .describe("Nickname or username"),
                                date_of_birth: zod
                                    .string()
                                    .date()
                                    .nullish()
                                    .describe("Date of birth in YYYY-MM-DD format"),
                                gender: zod
                                    .enum(["male", "female", "other", "prefer_not_to_say"])
                                    .nullish()
                                    .describe("Gender identity (optional for public display)"),
                                skin_type: zod
                                    .enum([
                                        "normal",
                                        "oily",
                                        "dry",
                                        "combination",
                                        "sensitive",
                                        "dehydrated_oily",
                                        "not_sure",
                                    ])
                                    .nullish()
                                    .describe("Skin type (preference profile)"),
                                skin_concerns: zod
                                    .array(zod.string())
                                    .optional()
                                    .describe("Skin concerns (multiple selection)"),
                                skin_tone: zod
                                    .enum(["very_fair", "fair", "medium", "tan", "deep", "not_sure"])
                                    .nullish(),
                                hair_type: zod
                                    .enum([
                                        "straight_coarse",
                                        "straight_soft",
                                        "wavy_coarse",
                                        "wavy_soft",
                                        "normal_hair",
                                        "not_sure",
                                    ])
                                    .nullish(),
                                makeup_frequency: zod.enum(["every_day", "occasionally", "rarely"]).nullish(),
                                hair_scalp_concerns: zod
                                    .array(zod.string())
                                    .optional()
                                    .describe("Hair & scalp concerns (multiple selection)"),
                                beauty_wellness_interests: zod
                                    .array(zod.string())
                                    .optional()
                                    .describe("Beauty & wellness interests (multiple selection)"),
                                lifestyle_environment: zod
                                    .array(zod.string())
                                    .optional()
                                    .describe("Lifestyle environment (multiple selection)"),
                                preferred_price_range: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserPreferredPriceRangeMax)
                                    .optional()
                                    .describe("Preferred price range (single select)"),
                                social_instagram: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserSocialInstagramMax)
                                    .optional(),
                                social_facebook: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserSocialFacebookMax)
                                    .optional(),
                                social_x: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserSocialXMax)
                                    .optional(),
                                social_tiktok: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserSocialTiktokMax)
                                    .optional(),
                                social_youtube: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserSocialYoutubeMax)
                                    .optional(),
                                social_website: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserSocialWebsiteMax)
                                    .optional(),
                                posts_count: zod.number().nullish(),
                                review_count: zod.number().nullish(),
                                followers_count: zod.number().optional(),
                                followings_count: zod.number().optional(),
                                recommend_rank: zod.number().nullish(),
                                avatar: zod
                                    .string()
                                    .max(getPostDetail200ResponseDataItemOneUserAvatarMax)
                                    .optional()
                                    .describe("Avatar image path"),
                                role: zod
                                    .enum(["admin", "editor", "moderator", "expert", "user"])
                                    .optional()
                                    .describe("User role"),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict()
                            .optional()
                            .describe("User information (sanitized for public display)"),
                        ogp_image_map: zod
                            .record(zod.string(), zod.string())
                            .optional()
                            .describe("Map of SNS URL to cached OGP image URL (populated on update response)"),
                    })
                    .strict()
                    .optional(),
            })
            .strict()
            .optional(),
    })
    .strict()
    .describe(
        "PostController::show() returns ServiceBase::getItem()'s {success, message, data} envelope, not a bare item.",
    );

/**
 * @summary Update post
 */
export const UpdateBoardPostParams = zod
    .object({
        board_id: zod.enum([
            "review",
            "tiktoks",
            "beauty_news",
            "editorial_reviews",
            "expert_reviews",
            "activities",
            "test_modal",
            "test_modeless",
            "guidance",
        ]),
        post_id: zod.number(),
    })
    .strict();

export const updateBoardPostBodyPostTitleMax = 300;

export const UpdateBoardPostBody = zod
    .object({
        post_title: zod.string().max(updateBoardPostBodyPostTitleMax).optional(),
        content: zod.string().min(1).describe("Post content (HTML)"),
        post_product_id: zod
            .number()
            .nullish()
            .describe("Product ID associated with the post. Null clears the linkage."),
        sns_shares: zod
            .array(zod.enum(["instagram", "tiktok"]))
            .optional()
            .describe("SNS share destinations requested for the post."),
        content_images: zod.array(zod.string()).optional().describe("Editor embedded images filenames"),
        sns_links: zod.array(zod.string().url()).optional().describe("SNS links (YouTube, Facebook, etc.)"),
        created_at: zod
            .string()
            .date()
            .optional()
            .describe("Override post created_at date (admin\/editor only). Stored as start-of-day."),
        post_status: zod
            .enum(["draft", "editing", "released", "rejected"])
            .optional()
            .describe("Post visibility status (admin\/editor only). If omitted, server keeps the existing value."),
    })
    .strict();

export const updateBoardPost200ResponseDataPostTitleMax = 300;

export const updateBoardPost200ResponseDataRecommendRankMax = 15;

export const updateBoardPost200ResponseDataCommentsItemUserDisplayNameMax = 50;

export const updateBoardPost200ResponseDataCommentsItemUserFirstNameMax = 100;

export const updateBoardPost200ResponseDataCommentsItemUserLastNameMax = 100;

export const updateBoardPost200ResponseDataCommentsItemUserNickNameMax = 50;

export const updateBoardPost200ResponseDataCommentsItemUserPreferredPriceRangeMax = 50;

export const updateBoardPost200ResponseDataCommentsItemUserSocialInstagramMax = 100;

export const updateBoardPost200ResponseDataCommentsItemUserSocialFacebookMax = 100;

export const updateBoardPost200ResponseDataCommentsItemUserSocialXMax = 100;

export const updateBoardPost200ResponseDataCommentsItemUserSocialTiktokMax = 100;

export const updateBoardPost200ResponseDataCommentsItemUserSocialYoutubeMax = 100;

export const updateBoardPost200ResponseDataCommentsItemUserSocialWebsiteMax = 500;

export const updateBoardPost200ResponseDataCommentsItemUserAvatarMax = 255;

export const updateBoardPost200ResponseDataReviewConfigOneReviewConfigNameMax = 70;

export const updateBoardPost200ResponseDataReviewQuestionsItemQuestionTextThMax = 200;

export const updateBoardPost200ResponseDataReviewQuestionsItemQuestionTextEnMax = 200;

export const updateBoardPost200ResponseDataReviewQuestionsItemQuestionTextJaMax = 200;

export const updateBoardPost200ResponseDataReviewDataOneAnswersItemQuestionTextThMax = 200;

export const updateBoardPost200ResponseDataReviewDataOneAnswersItemQuestionTextEnMax = 200;

export const updateBoardPost200ResponseDataReviewDataOneAnswersItemQuestionTextJaMax = 200;

export const updateBoardPost200ResponseDataAdsItemImagePathMax = 150;

export const updateBoardPost200ResponseDataAdsItemAdTitleMax = 40;

export const updateBoardPost200ResponseDataAdsItemAdBodyMax = 130;

export const updateBoardPost200ResponseDataAdsItemLinkUrlMax = 500;

export const updateBoardPost200ResponseDataProductOneProductNameMax = 255;

export const updateBoardPost200ResponseDataProductOneBrandNameMax = 100;

export const updateBoardPost200ResponseDataProductOneBrandBrandNameMax = 100;

export const updateBoardPost200ResponseDataProductOneCategoryNameMax = 100;

export const updateBoardPost200ResponseDataProductOneCategoryCategoryNameMax = 100;

export const updateBoardPost200ResponseDataProductOneCategoryParentCategoryNameMax = 100;

export const updateBoardPost200ResponseDataProductOneImagePathMax = 150;

export const updateBoardPost200ResponseDataProductOneReviewCountDefault = 0;
export const updateBoardPost200ResponseDataProductOnePriceUnitMax = 10;

export const updateBoardPost200ResponseDataProductOneUnitMax = 100;

export const updateBoardPost200ResponseDataProductOneKeywordMax = 500;

export const updateBoardPost200ResponseDataProductOneParentCategoryNameMax = 100;

export const updateBoardPost200ResponseDataProductOneReviewSummaryEnMax = 500;

export const updateBoardPost200ResponseDataProductOneReviewSummaryJaMax = 500;

export const updateBoardPost200ResponseDataRecommendedProductsItemProductNameMax = 255;

export const updateBoardPost200ResponseDataRecommendedProductsItemBrandNameMax = 100;

export const updateBoardPost200ResponseDataRecommendedProductsItemBrandBrandNameMax = 100;

export const updateBoardPost200ResponseDataRecommendedProductsItemCategoryNameMax = 100;

export const updateBoardPost200ResponseDataRecommendedProductsItemCategoryCategoryNameMax = 100;

export const updateBoardPost200ResponseDataRecommendedProductsItemCategoryParentCategoryNameMax = 100;

export const updateBoardPost200ResponseDataRecommendedProductsItemImagePathMax = 150;

export const updateBoardPost200ResponseDataRecommendedProductsItemReviewCountDefault = 0;
export const updateBoardPost200ResponseDataRecommendedProductsItemPriceUnitMax = 10;

export const updateBoardPost200ResponseDataRecommendedProductsItemUnitMax = 100;

export const updateBoardPost200ResponseDataRecommendedProductsItemKeywordMax = 500;

export const updateBoardPost200ResponseDataRecommendedProductsItemParentCategoryNameMax = 100;

export const updateBoardPost200ResponseDataRecommendedProductsItemReviewSummaryEnMax = 500;

export const updateBoardPost200ResponseDataRecommendedProductsItemReviewSummaryJaMax = 500;

export const updateBoardPost200ResponseDataBoardNameThMax = 100;

export const updateBoardPost200ResponseDataBoardNameEnMax = 100;

export const updateBoardPost200ResponseDataBoardNameJaMax = 100;

export const updateBoardPost200ResponseDataBoardDescriptionThMax = 255;

export const updateBoardPost200ResponseDataBoardDescriptionEnMax = 255;

export const updateBoardPost200ResponseDataBoardDescriptionJaMax = 255;

export const updateBoardPost200ResponseDataBoardFixedTitleMax = 500;

export const updateBoardPost200ResponseDataUserDisplayNameMax = 50;

export const updateBoardPost200ResponseDataUserFirstNameMax = 100;

export const updateBoardPost200ResponseDataUserLastNameMax = 100;

export const updateBoardPost200ResponseDataUserNickNameMax = 50;

export const updateBoardPost200ResponseDataUserPreferredPriceRangeMax = 50;

export const updateBoardPost200ResponseDataUserSocialInstagramMax = 100;

export const updateBoardPost200ResponseDataUserSocialFacebookMax = 100;

export const updateBoardPost200ResponseDataUserSocialXMax = 100;

export const updateBoardPost200ResponseDataUserSocialTiktokMax = 100;

export const updateBoardPost200ResponseDataUserSocialYoutubeMax = 100;

export const updateBoardPost200ResponseDataUserSocialWebsiteMax = 500;

export const updateBoardPost200ResponseDataUserAvatarMax = 255;

export const UpdateBoardPost200Response = zod
    .object({
        success: zod
            .union([zod.literal(0), zod.literal(1)])
            .optional()
            .describe("Success flag: 1 = success, 0 = failure"),
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .optional(),
        data: zod
            .object({
                post_id: zod.number().optional().describe("Numeric primary key (DB PK)"),
                board_id: zod
                    .enum([
                        "review",
                        "tiktoks",
                        "beauty_news",
                        "editorial_reviews",
                        "expert_reviews",
                        "activities",
                        "test_modal",
                        "test_modeless",
                        "guidance",
                    ])
                    .optional()
                    .describe("Unique string identifier for the board (e.g., 'xxxboard')"),
                user_id: zod.number().optional(),
                post_title: zod.string().max(updateBoardPost200ResponseDataPostTitleMax).optional(),
                content: zod.string().optional(),
                post_product_id: zod.number().nullish().describe("Product ID for review posts"),
                product_brand_name: zod.string().nullish().describe("Product brand name (flattened)"),
                product_category_id: zod.number().nullish().describe("Product category ID (flattened)"),
                product_category_name: zod.string().nullish().describe("Product category name (flattened)"),
                product_parent_category_id: zod
                    .number()
                    .nullish()
                    .describe("Parent category ID (derived from product category)"),
                product_parent_category_name: zod.string().nullish().describe("Parent category name (flattened)"),
                recommend_rank: zod
                    .number()
                    .min(1)
                    .max(updateBoardPost200ResponseDataRecommendRankMax)
                    .nullish()
                    .describe("Recommend rank for crown display (ISS:04-047)"),
                monthly_rank: zod
                    .number()
                    .min(1)
                    .nullish()
                    .describe(
                        "Monthly ranking rank (materialized; derived from product monthly_rank for review posts) (ISS:04-048)",
                    ),
                product_review_count: zod
                    .number()
                    .optional()
                    .describe("Total review count for this product (cached from product data)"),
                product_rating_average: zod
                    .number()
                    .optional()
                    .describe("Average rating for this product (cached from product data)"),
                user_rating_average: zod
                    .number()
                    .optional()
                    .describe("Current user's average rating calculated from their review answers"),
                attached_images: zod.array(zod.string()).optional().describe("Images uploaded via button\/form"),
                attached_videos: zod.array(zod.string()).optional().describe("Videos uploaded via button\/form"),
                content_images: zod
                    .array(zod.string())
                    .optional()
                    .describe("Image filenames extracted from content (rich text embedded images)"),
                sns_shares: zod
                    .array(zod.enum(["instagram", "tiktok"]))
                    .optional()
                    .describe("SNS share destinations requested for the post."),
                sns_links: zod
                    .array(zod.string().url())
                    .optional()
                    .describe("SNS platform links (Facebook, YouTube, etc.) stored as structured data"),
                likes_count: zod.number().nullish().describe("Total number of likes"),
                likes: zod
                    .array(
                        zod
                            .object({
                                like_id: zod.number().optional().describe("Unique like identifier"),
                                post_id: zod.number().optional(),
                                comment_id: zod.number().nullish(),
                                user_id: zod
                                    .number()
                                    .optional()
                                    .describe("User nickname for display (nick_name field)"),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                                updated_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict(),
                    )
                    .optional()
                    .describe("Detailed likes with user info"),
                reads_count: zod.number().nullish().describe("Total number of reads"),
                reads: zod
                    .array(
                        zod
                            .object({
                                read_id: zod.number().optional().describe("Unique read identifier"),
                                user_id: zod.number().optional().describe("User ID who read the post"),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict(),
                    )
                    .optional()
                    .describe("Detailed reads with user info"),
                comments_count: zod.number().nullish().describe("Total number of comments"),
                comments: zod
                    .array(
                        zod
                            .object({
                                comment_id: zod.number().optional(),
                                post_id: zod.number().optional(),
                                board_id: zod
                                    .enum([
                                        "review",
                                        "tiktoks",
                                        "beauty_news",
                                        "editorial_reviews",
                                        "expert_reviews",
                                        "activities",
                                        "test_modal",
                                        "test_modeless",
                                        "guidance",
                                    ])
                                    .optional(),
                                user_id: zod.number().optional(),
                                content: zod.string().optional(),
                                parent_comment_id: zod
                                    .number()
                                    .nullish()
                                    .describe("Parent comment ID (null allowed in implementation for root comments)"),
                                replies_count: zod.number().optional(),
                                like_count: zod.number().optional().describe("Deprecated - use likes_count instead"),
                                likes_count: zod.number().nullish().describe("Total number of likes for this comment"),
                                is_liked: zod
                                    .boolean()
                                    .optional()
                                    .describe("Whether current user has liked this comment"),
                                likes: zod
                                    .array(
                                        zod
                                            .object({
                                                like_id: zod.number().optional().describe("Unique like identifier"),
                                                post_id: zod.number().optional(),
                                                comment_id: zod.number().nullish(),
                                                user_id: zod
                                                    .number()
                                                    .optional()
                                                    .describe("User nickname for display (nick_name field)"),
                                                created_at: zod.string().datetime({ offset: true }).optional(),
                                                updated_at: zod.string().datetime({ offset: true }).optional(),
                                            })
                                            .strict(),
                                    )
                                    .optional()
                                    .describe("Detailed likes with user info"),
                                replies: zod
                                    .array(zod.object({}).passthrough())
                                    .optional()
                                    .describe("Nested child comments for this comment"),
                                delete_flag: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                                updated_at: zod.string().datetime({ offset: true }).optional(),
                                user: zod
                                    .object({
                                        user_id: zod.number().optional(),
                                        display_name: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserDisplayNameMax)
                                            .optional()
                                            .describe("Full display name"),
                                        first_name: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserFirstNameMax)
                                            .optional()
                                            .describe("First name"),
                                        last_name: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserLastNameMax)
                                            .optional()
                                            .describe("Last name"),
                                        nick_name: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserNickNameMax)
                                            .optional()
                                            .describe("Nickname or username"),
                                        date_of_birth: zod
                                            .string()
                                            .date()
                                            .nullish()
                                            .describe("Date of birth in YYYY-MM-DD format"),
                                        gender: zod
                                            .enum(["male", "female", "other", "prefer_not_to_say"])
                                            .nullish()
                                            .describe("Gender identity (optional for public display)"),
                                        skin_type: zod
                                            .enum([
                                                "normal",
                                                "oily",
                                                "dry",
                                                "combination",
                                                "sensitive",
                                                "dehydrated_oily",
                                                "not_sure",
                                            ])
                                            .nullish()
                                            .describe("Skin type (preference profile)"),
                                        skin_concerns: zod
                                            .array(zod.string())
                                            .optional()
                                            .describe("Skin concerns (multiple selection)"),
                                        skin_tone: zod
                                            .enum(["very_fair", "fair", "medium", "tan", "deep", "not_sure"])
                                            .nullish(),
                                        hair_type: zod
                                            .enum([
                                                "straight_coarse",
                                                "straight_soft",
                                                "wavy_coarse",
                                                "wavy_soft",
                                                "normal_hair",
                                                "not_sure",
                                            ])
                                            .nullish(),
                                        makeup_frequency: zod.enum(["every_day", "occasionally", "rarely"]).nullish(),
                                        hair_scalp_concerns: zod
                                            .array(zod.string())
                                            .optional()
                                            .describe("Hair & scalp concerns (multiple selection)"),
                                        beauty_wellness_interests: zod
                                            .array(zod.string())
                                            .optional()
                                            .describe("Beauty & wellness interests (multiple selection)"),
                                        lifestyle_environment: zod
                                            .array(zod.string())
                                            .optional()
                                            .describe("Lifestyle environment (multiple selection)"),
                                        preferred_price_range: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserPreferredPriceRangeMax)
                                            .optional()
                                            .describe("Preferred price range (single select)"),
                                        social_instagram: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserSocialInstagramMax)
                                            .optional(),
                                        social_facebook: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserSocialFacebookMax)
                                            .optional(),
                                        social_x: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserSocialXMax)
                                            .optional(),
                                        social_tiktok: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserSocialTiktokMax)
                                            .optional(),
                                        social_youtube: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserSocialYoutubeMax)
                                            .optional(),
                                        social_website: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserSocialWebsiteMax)
                                            .optional(),
                                        posts_count: zod.number().nullish(),
                                        review_count: zod.number().nullish(),
                                        followers_count: zod.number().optional(),
                                        followings_count: zod.number().optional(),
                                        recommend_rank: zod.number().nullish(),
                                        avatar: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataCommentsItemUserAvatarMax)
                                            .optional()
                                            .describe("Avatar image path"),
                                        role: zod
                                            .enum(["admin", "editor", "moderator", "expert", "user"])
                                            .optional()
                                            .describe("User role"),
                                        created_at: zod.string().datetime({ offset: true }).optional(),
                                    })
                                    .strict()
                                    .optional()
                                    .describe("User information for comment author"),
                            })
                            .strict(),
                    )
                    .optional()
                    .describe("List of comments with user and like information"),
                review_enabled: zod
                    .union([zod.literal(0), zod.literal(1)])
                    .optional()
                    .describe("Whether reviews are enabled for this board"),
                review_config: zod
                    .union([
                        zod
                            .object({
                                review_config_id: zod.number().optional(),
                                review_config_name: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataReviewConfigOneReviewConfigNameMax)
                                    .optional(),
                                general_description: zod.string().optional(),
                                is_active: zod.number().optional(),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                                updated_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict(),
                        zod.null(),
                    ])
                    .optional()
                    .describe("Review configuration for this board"),
                review_questions: zod
                    .array(
                        zod
                            .object({
                                review_question_id: zod.number().optional(),
                                review_config_id: zod.number().optional(),
                                question_text_th: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataReviewQuestionsItemQuestionTextThMax)
                                    .optional(),
                                question_text_en: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataReviewQuestionsItemQuestionTextEnMax)
                                    .optional(),
                                question_text_ja: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataReviewQuestionsItemQuestionTextJaMax)
                                    .optional(),
                                data_type: zod.enum(["rating", "single_select", "multi_select", "text"]).optional(),
                                option_th: zod.array(zod.string()).optional(),
                                option_en: zod.array(zod.string()).optional(),
                                option_ja: zod.array(zod.string()).optional(),
                                is_required: zod.number().optional(),
                                is_active: zod.number().optional(),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                                updated_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict(),
                    )
                    .optional()
                    .describe("List of review questions for this board"),
                review_data: zod
                    .union([
                        zod
                            .object({
                                review_answer_id: zod.number().optional(),
                                review_config_id: zod.number().optional(),
                                answers: zod
                                    .array(
                                        zod
                                            .object({
                                                review_question_id: zod.number().optional(),
                                                answer_value: zod.string().optional(),
                                                question_text_th: zod
                                                    .string()
                                                    .max(
                                                        updateBoardPost200ResponseDataReviewDataOneAnswersItemQuestionTextThMax,
                                                    )
                                                    .optional(),
                                                question_text_en: zod
                                                    .string()
                                                    .max(
                                                        updateBoardPost200ResponseDataReviewDataOneAnswersItemQuestionTextEnMax,
                                                    )
                                                    .optional(),
                                                question_text_ja: zod
                                                    .string()
                                                    .max(
                                                        updateBoardPost200ResponseDataReviewDataOneAnswersItemQuestionTextJaMax,
                                                    )
                                                    .optional(),
                                                question_type: zod.string().optional(),
                                                option_th: zod.array(zod.string()).optional(),
                                                option_en: zod.array(zod.string()).optional(),
                                                option_ja: zod.array(zod.string()).optional(),
                                                answer_value_th: zod.string().optional(),
                                                answer_value_en: zod.string().optional(),
                                                answer_value_ja: zod.string().optional(),
                                            })
                                            .strict()
                                            .describe(
                                                "Question text\/options\/localized answer values are denormalized onto each answer for display convenience.",
                                            ),
                                    )
                                    .optional(),
                            })
                            .strict()
                            .describe("Current user's review data for this post (null if not reviewed)"),
                        zod.null(),
                    ])
                    .optional(),
                reviews_count: zod.number().optional().describe("Total number of reviews for this post"),
                average_rating: zod.number().nullish().describe("Average rating score (null if no ratings)"),
                rating_distribution: zod
                    .object({
                        "1": zod.number().optional(),
                        "2": zod.number().optional(),
                        "3": zod.number().optional(),
                        "4": zod.number().optional(),
                        "5": zod.number().optional(),
                    })
                    .strict()
                    .optional()
                    .describe("Distribution of rating values"),
                has_user_reviewed: zod.boolean().optional().describe("Whether the current user has reviewed this post"),
                ads: zod
                    .array(
                        zod
                            .object({
                                ad_id: zod.number().optional(),
                                ad_type: zod
                                    .enum([
                                        "PLACE_HOLDER",
                                        "PRODUCT_LINKER",
                                        "PRODUCT_LINKER_OGP",
                                        "PRODUCT_CARD",
                                        "TOP_BANNER",
                                        "SECTION_BANNER",
                                        "USERS_BANNER",
                                        "USERS_BANNER_RECTANGLE",
                                        "AUTH_BANNER",
                                    ])
                                    .optional(),
                                image_path: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataAdsItemImagePathMax)
                                    .optional(),
                                ad_title: zod.string().max(updateBoardPost200ResponseDataAdsItemAdTitleMax).optional(),
                                ad_body: zod.string().max(updateBoardPost200ResponseDataAdsItemAdBodyMax).optional(),
                                link_url: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataAdsItemLinkUrlMax)
                                    .optional()
                                    .describe(
                                        'URL, or \"\" when unset (per project convention for absent string data — see CLAUDE.md).',
                                    ),
                                is_internal_modal_link: zod
                                    .boolean()
                                    .optional()
                                    .describe(
                                        "True when link_url points at an internal post whose board is modal_usage=MODAL — the ad should open that board's in-page modal instead of a new tab (see AdService::resolveInternalModalLink()).",
                                    ),
                                modal_board_id: zod
                                    .string()
                                    .nullish()
                                    .describe(
                                        "board_id to open in-modal when is_internal_modal_link is true, otherwise null.",
                                    ),
                                modal_post_id: zod
                                    .number()
                                    .nullish()
                                    .describe(
                                        "post_id to open in-modal when is_internal_modal_link is true, otherwise null.",
                                    ),
                                ad_product_id: zod.number().nullish(),
                                display_rate_percent: zod
                                    .number()
                                    .nullish()
                                    .describe(
                                        "Display probability weight (0-100) among ads competing for the same target",
                                    ),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                                updated_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict()
                            .describe("Ad item embedded in a post response (pre-selected by ad target type)"),
                    )
                    .optional()
                    .describe(
                        "Ads pre-selected for this post. PLACE_HOLDER type is included when the post has no media (text-only). PRODUCT_LINKER \/ PRODUCT_LINKER_OGP type is included when the post has no linked product.\n",
                    ),
                product: zod
                    .union([
                        zod
                            .object({
                                product_id: zod.number().optional().describe("Unique product identifier"),
                                product_name: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataProductOneProductNameMax)
                                    .optional()
                                    .describe("Product name"),
                                product_status: zod
                                    .enum(["draft", "editing", "released", "duplicated", "rejected", "dummy", "brand"])
                                    .optional()
                                    .describe("Product publication status"),
                                brand_id: zod.number().nullish().describe("Brand ID (flattened)"),
                                brand_name: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataProductOneBrandNameMax)
                                    .optional()
                                    .describe("Brand name (flattened)"),
                                brand: zod
                                    .object({
                                        brand_id: zod.number().nullish(),
                                        brand_name: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataProductOneBrandBrandNameMax)
                                            .optional(),
                                    })
                                    .strict()
                                    .nullish()
                                    .describe("Nested brand data"),
                                category_id: zod.number().nullish().describe("Category ID (flattened)"),
                                category_name: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataProductOneCategoryNameMax)
                                    .optional()
                                    .describe("Category name (flattened)"),
                                category: zod
                                    .object({
                                        category_id: zod.number().nullish(),
                                        category_name: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataProductOneCategoryCategoryNameMax)
                                            .optional(),
                                        parent_category_id: zod.number().nullish(),
                                        parent_category_name: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataProductOneCategoryParentCategoryNameMax)
                                            .optional(),
                                    })
                                    .strict()
                                    .nullish()
                                    .describe("Nested category data"),
                                image_url: zod.string().optional().describe("Product image URL"),
                                image: zod.string().optional().describe("Product image filename"),
                                image_path: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataProductOneImagePathMax)
                                    .optional()
                                    .describe("Product image path"),
                                review_count: zod
                                    .number()
                                    .nullish()
                                    .default(updateBoardPost200ResponseDataProductOneReviewCountDefault)
                                    .describe("Total review count"),
                                average_rating: zod.number().nullish().describe("Average rating score"),
                                recommend_rank: zod.number().nullish().describe("Recommendation rank"),
                                general_description: zod
                                    .string()
                                    .optional()
                                    .describe("General product description (post-product-linker preview text)"),
                                product_note: zod.string().optional(),
                                product_url: zod.string().optional(),
                                price: zod.number().nullish(),
                                price_unit: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataProductOnePriceUnitMax)
                                    .optional(),
                                group_id: zod.union([zod.number(), zod.string()]).nullish(),
                                unit: zod.string().max(updateBoardPost200ResponseDataProductOneUnitMax).optional(),
                                how_to_use: zod.string().optional(),
                                ingredients: zod.string().optional(),
                                keyword: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataProductOneKeywordMax)
                                    .optional(),
                                created_by: zod.number().nullish(),
                                updated_by: zod.number().nullish(),
                                monthly_rank: zod.number().nullish(),
                                manual_score_offset: zod.number().nullish(),
                                parent_category_id: zod.number().nullish(),
                                parent_category_name: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataProductOneParentCategoryNameMax)
                                    .optional(),
                                likes_count: zod.number().nullish(),
                                comments_count: zod.number().nullish(),
                                reads_count: zod.number().nullish(),
                                review_count_guest: zod.number().nullish(),
                                posts_count: zod.number().nullish(),
                                posts_count_guest: zod.number().nullish(),
                                positive_points_th: zod.array(zod.string()).optional(),
                                negative_points_th: zod.array(zod.string()).optional(),
                                positive_points_en: zod.array(zod.string()).optional(),
                                negative_points_en: zod.array(zod.string()).optional(),
                                positive_points_ja: zod.array(zod.string()).optional(),
                                negative_points_ja: zod.array(zod.string()).optional(),
                                review_summary_th: zod.string().optional(),
                                review_summary_en: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataProductOneReviewSummaryEnMax)
                                    .optional(),
                                review_summary_ja: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataProductOneReviewSummaryJaMax)
                                    .optional(),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                                updated_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict()
                            .describe("Linked product data embedded in a post response (pre-fetched for SSR)."),
                        zod.null(),
                    ])
                    .optional()
                    .describe(
                        "Linked product data pre-fetched for SSR rendering of the product linker. Null when the post has no linked product.\n",
                    ),
                recommended_products: zod
                    .array(
                        zod
                            .object({
                                product_id: zod.number().optional().describe("Unique product identifier"),
                                product_name: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataRecommendedProductsItemProductNameMax)
                                    .optional()
                                    .describe("Product name"),
                                product_status: zod
                                    .enum(["draft", "editing", "released", "duplicated", "rejected", "dummy", "brand"])
                                    .optional()
                                    .describe("Product publication status"),
                                brand_id: zod.number().nullish().describe("Brand ID (flattened)"),
                                brand_name: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataRecommendedProductsItemBrandNameMax)
                                    .optional()
                                    .describe("Brand name (flattened)"),
                                brand: zod
                                    .object({
                                        brand_id: zod.number().nullish(),
                                        brand_name: zod
                                            .string()
                                            .max(updateBoardPost200ResponseDataRecommendedProductsItemBrandBrandNameMax)
                                            .optional(),
                                    })
                                    .strict()
                                    .nullish()
                                    .describe("Nested brand data"),
                                category_id: zod.number().nullish().describe("Category ID (flattened)"),
                                category_name: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataRecommendedProductsItemCategoryNameMax)
                                    .optional()
                                    .describe("Category name (flattened)"),
                                category: zod
                                    .object({
                                        category_id: zod.number().nullish(),
                                        category_name: zod
                                            .string()
                                            .max(
                                                updateBoardPost200ResponseDataRecommendedProductsItemCategoryCategoryNameMax,
                                            )
                                            .optional(),
                                        parent_category_id: zod.number().nullish(),
                                        parent_category_name: zod
                                            .string()
                                            .max(
                                                updateBoardPost200ResponseDataRecommendedProductsItemCategoryParentCategoryNameMax,
                                            )
                                            .optional(),
                                    })
                                    .strict()
                                    .nullish()
                                    .describe("Nested category data"),
                                image_url: zod.string().optional().describe("Product image URL"),
                                image: zod.string().optional().describe("Product image filename"),
                                image_path: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataRecommendedProductsItemImagePathMax)
                                    .optional()
                                    .describe("Product image path"),
                                review_count: zod
                                    .number()
                                    .nullish()
                                    .default(updateBoardPost200ResponseDataRecommendedProductsItemReviewCountDefault)
                                    .describe("Total review count"),
                                average_rating: zod.number().nullish().describe("Average rating score"),
                                recommend_rank: zod.number().nullish().describe("Recommendation rank"),
                                general_description: zod
                                    .string()
                                    .optional()
                                    .describe("General product description (post-product-linker preview text)"),
                                product_note: zod.string().optional(),
                                product_url: zod.string().optional(),
                                price: zod.number().nullish(),
                                price_unit: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataRecommendedProductsItemPriceUnitMax)
                                    .optional(),
                                group_id: zod.union([zod.number(), zod.string()]).nullish(),
                                unit: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataRecommendedProductsItemUnitMax)
                                    .optional(),
                                how_to_use: zod.string().optional(),
                                ingredients: zod.string().optional(),
                                keyword: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataRecommendedProductsItemKeywordMax)
                                    .optional(),
                                created_by: zod.number().nullish(),
                                updated_by: zod.number().nullish(),
                                monthly_rank: zod.number().nullish(),
                                manual_score_offset: zod.number().nullish(),
                                parent_category_id: zod.number().nullish(),
                                parent_category_name: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataRecommendedProductsItemParentCategoryNameMax)
                                    .optional(),
                                likes_count: zod.number().nullish(),
                                comments_count: zod.number().nullish(),
                                reads_count: zod.number().nullish(),
                                review_count_guest: zod.number().nullish(),
                                posts_count: zod.number().nullish(),
                                posts_count_guest: zod.number().nullish(),
                                positive_points_th: zod.array(zod.string()).optional(),
                                negative_points_th: zod.array(zod.string()).optional(),
                                positive_points_en: zod.array(zod.string()).optional(),
                                negative_points_en: zod.array(zod.string()).optional(),
                                positive_points_ja: zod.array(zod.string()).optional(),
                                negative_points_ja: zod.array(zod.string()).optional(),
                                review_summary_th: zod.string().optional(),
                                review_summary_en: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataRecommendedProductsItemReviewSummaryEnMax)
                                    .optional(),
                                review_summary_ja: zod
                                    .string()
                                    .max(updateBoardPost200ResponseDataRecommendedProductsItemReviewSummaryJaMax)
                                    .optional(),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                                updated_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict()
                            .describe("Linked product data embedded in a post response (pre-fetched for SSR)."),
                    )
                    .optional()
                    .describe(
                        "Products recommended below the post's content: other products in the linked product's category, falling back to the same Trending Products selection shown on products\/ when the post has no linked product or its category has no other products. Empty array when none available.\n",
                    ),
                delete_flag: zod
                    .union([zod.literal(0), zod.literal(1)])
                    .optional()
                    .describe("1 if deleted (soft delete), 0 otherwise"),
                post_status: zod
                    .enum(["draft", "editing", "released", "rejected"])
                    .optional()
                    .describe(
                        "Post visibility status: released = publicly visible, hidden = visible to admin\/editor only",
                    ),
                created_at: zod.string().datetime({ offset: true }).optional(),
                updated_at: zod.string().datetime({ offset: true }).optional(),
                board: zod
                    .object({
                        board_config_id: zod.number().optional(),
                        board_id: zod
                            .enum([
                                "review",
                                "tiktoks",
                                "beauty_news",
                                "editorial_reviews",
                                "expert_reviews",
                                "activities",
                                "test_modal",
                                "test_modeless",
                                "guidance",
                            ])
                            .optional(),
                        is_active: zod.number().optional(),
                        allowed_read_roles: zod.array(zod.string()).optional(),
                        allowed_write_roles: zod.array(zod.string()).optional(),
                        allowed_like_roles: zod.array(zod.string()).optional(),
                        allowed_comment_roles: zod.array(zod.string()).optional(),
                        comment_enabled: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                        board_type: zod.enum(["general", "news", "article", "review"]).optional(),
                        name_th: zod.string().max(updateBoardPost200ResponseDataBoardNameThMax).optional(),
                        name_en: zod.string().max(updateBoardPost200ResponseDataBoardNameEnMax).optional(),
                        name_ja: zod.string().max(updateBoardPost200ResponseDataBoardNameJaMax).optional(),
                        description_th: zod
                            .string()
                            .max(updateBoardPost200ResponseDataBoardDescriptionThMax)
                            .optional(),
                        description_en: zod
                            .string()
                            .max(updateBoardPost200ResponseDataBoardDescriptionEnMax)
                            .optional(),
                        description_ja: zod
                            .string()
                            .max(updateBoardPost200ResponseDataBoardDescriptionJaMax)
                            .optional(),
                        list_style: zod
                            .enum(["CARD", "LIST", "ONE_IMAGE_CARD", "TILES"])
                            .optional()
                            .describe("Board list presentation style. LIST is reserved for future implementation."),
                        modal_usage: zod
                            .enum(["MODELESS", "MODELESS_TARGET_BLANK", "MODAL", "DIRECT_MAXIMIZE"])
                            .optional()
                            .describe(
                                "Post details display method: MODELESS = same-tab page navigation, MODELESS_TARGET_BLANK = new-tab navigation, MODAL = in-page modal",
                            ),
                        modal_size: zod.enum(["N/A", "small", "medium", "large", "extra-large"]).optional(),
                        title_enabled: zod
                            .union([zod.literal(0), zod.literal(1)])
                            .optional()
                            .describe("Post title input visibility: 1 = enabled, 0 = hidden\/fixed"),
                        fixed_title: zod
                            .string()
                            .max(updateBoardPost200ResponseDataBoardFixedTitleMax)
                            .optional()
                            .describe("Fixed title value used when title input is disabled"),
                        product_linker_show: zod
                            .enum(["SHOW", "HIDE"])
                            .optional()
                            .describe("Product linker section visibility in post form: SHOW = visible, HIDE = hidden"),
                        target_product_show: zod
                            .enum(["SHOW", "HIDE"])
                            .optional()
                            .describe(
                                "Target Product preview visibility on post detail page: SHOW = visible, HIDE = hidden",
                            ),
                        ogp_preview_show: zod
                            .enum(["SHOW", "HIDE"])
                            .optional()
                            .describe(
                                "SNS link OGP preview visibility on post detail page: SHOW = visible, HIDE = hidden",
                            ),
                        user_info_show: zod
                            .enum(["SHOW", "HIDE"])
                            .optional()
                            .describe(
                                "User info section visibility on post card and detail: SHOW = visible, HIDE = hidden",
                            ),
                        date_show: zod
                            .enum(["SHOW", "HIDE"])
                            .nullish()
                            .describe(
                                "Post date visibility in the top-right corner of the post detail view: SHOW = visible, HIDE = hidden",
                            ),
                        show_content: zod.enum(["SHOW", "HIDE"]).optional(),
                        rating_overlay_show: zod.enum(["SHOW", "HIDE"]).optional(),
                        review_enabled: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                        review_config_id: zod.number().nullish(),
                        created_at: zod.string().datetime({ offset: true }).optional(),
                        updated_at: zod.string().datetime({ offset: true }).optional(),
                    })
                    .strict()
                    .optional()
                    .describe("Board configuration and attributes"),
                user: zod
                    .object({
                        user_id: zod.number().optional(),
                        display_name: zod
                            .string()
                            .max(updateBoardPost200ResponseDataUserDisplayNameMax)
                            .optional()
                            .describe("Full display name"),
                        first_name: zod
                            .string()
                            .max(updateBoardPost200ResponseDataUserFirstNameMax)
                            .optional()
                            .describe("First name"),
                        last_name: zod
                            .string()
                            .max(updateBoardPost200ResponseDataUserLastNameMax)
                            .optional()
                            .describe("Last name"),
                        nick_name: zod
                            .string()
                            .max(updateBoardPost200ResponseDataUserNickNameMax)
                            .optional()
                            .describe("Nickname or username"),
                        date_of_birth: zod.string().date().nullish().describe("Date of birth in YYYY-MM-DD format"),
                        gender: zod
                            .enum(["male", "female", "other", "prefer_not_to_say"])
                            .nullish()
                            .describe("Gender identity (optional for public display)"),
                        skin_type: zod
                            .enum(["normal", "oily", "dry", "combination", "sensitive", "dehydrated_oily", "not_sure"])
                            .nullish()
                            .describe("Skin type (preference profile)"),
                        skin_concerns: zod
                            .array(zod.string())
                            .optional()
                            .describe("Skin concerns (multiple selection)"),
                        skin_tone: zod.enum(["very_fair", "fair", "medium", "tan", "deep", "not_sure"]).nullish(),
                        hair_type: zod
                            .enum([
                                "straight_coarse",
                                "straight_soft",
                                "wavy_coarse",
                                "wavy_soft",
                                "normal_hair",
                                "not_sure",
                            ])
                            .nullish(),
                        makeup_frequency: zod.enum(["every_day", "occasionally", "rarely"]).nullish(),
                        hair_scalp_concerns: zod
                            .array(zod.string())
                            .optional()
                            .describe("Hair & scalp concerns (multiple selection)"),
                        beauty_wellness_interests: zod
                            .array(zod.string())
                            .optional()
                            .describe("Beauty & wellness interests (multiple selection)"),
                        lifestyle_environment: zod
                            .array(zod.string())
                            .optional()
                            .describe("Lifestyle environment (multiple selection)"),
                        preferred_price_range: zod
                            .string()
                            .max(updateBoardPost200ResponseDataUserPreferredPriceRangeMax)
                            .optional()
                            .describe("Preferred price range (single select)"),
                        social_instagram: zod
                            .string()
                            .max(updateBoardPost200ResponseDataUserSocialInstagramMax)
                            .optional(),
                        social_facebook: zod
                            .string()
                            .max(updateBoardPost200ResponseDataUserSocialFacebookMax)
                            .optional(),
                        social_x: zod.string().max(updateBoardPost200ResponseDataUserSocialXMax).optional(),
                        social_tiktok: zod.string().max(updateBoardPost200ResponseDataUserSocialTiktokMax).optional(),
                        social_youtube: zod.string().max(updateBoardPost200ResponseDataUserSocialYoutubeMax).optional(),
                        social_website: zod.string().max(updateBoardPost200ResponseDataUserSocialWebsiteMax).optional(),
                        posts_count: zod.number().nullish(),
                        review_count: zod.number().nullish(),
                        followers_count: zod.number().optional(),
                        followings_count: zod.number().optional(),
                        recommend_rank: zod.number().nullish(),
                        avatar: zod
                            .string()
                            .max(updateBoardPost200ResponseDataUserAvatarMax)
                            .optional()
                            .describe("Avatar image path"),
                        role: zod
                            .enum(["admin", "editor", "moderator", "expert", "user"])
                            .optional()
                            .describe("User role"),
                        created_at: zod.string().datetime({ offset: true }).optional(),
                    })
                    .strict()
                    .optional()
                    .describe("User information (sanitized for public display)"),
                ogp_image_map: zod
                    .record(zod.string(), zod.string())
                    .optional()
                    .describe("Map of SNS URL to cached OGP image URL (populated on update response)"),
            })
            .strict()
            .optional(),
    })
    .strict();

/**
 * @summary Delete board
 */
export const DeleteBoardPostParams = zod
    .object({
        board_id: zod.enum([
            "review",
            "tiktoks",
            "beauty_news",
            "editorial_reviews",
            "expert_reviews",
            "activities",
            "test_modal",
            "test_modeless",
            "guidance",
        ]),
        post_id: zod.number(),
    })
    .strict();

export const DeleteBoardPost200Response = zod
    .object({
        success: zod
            .union([zod.literal(0), zod.literal(1)])
            .optional()
            .describe("Success flag: 1 = success, 0 = failure"),
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .optional(),
        data: zod.object({}).passthrough().optional(),
    })
    .strict();

/**
 * Add a like to a post. User ID is obtained from authentication context. CSRF token is sent via X-CSRF-TOKEN header.
 * @summary Add like to post
 */

export const AddLikeToPostParams = zod
    .object({
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .describe("Board identifier"),
        post_id: zod.number().min(1).describe("Post identifier"),
    })
    .strict();

export const AddLikeToPost201Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        likes_count: zod.number().nullish(),
    })
    .strict();

export const AddLikeToPost400Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const AddLikeToPost401Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const AddLikeToPost403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const AddLikeToPost404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const AddLikeToPost500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

/**
 * Remove a like from a post. User ID is obtained from authentication context. CSRF token is sent via X-CSRF-TOKEN header.
 * @summary Remove like from post
 */

export const RemoveLikeFromPostParams = zod
    .object({
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .describe("Board identifier"),
        post_id: zod.number().min(1).describe("Post identifier"),
    })
    .strict();

export const RemoveLikeFromPost200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        likes_count: zod.number().nullish(),
    })
    .strict();

export const RemoveLikeFromPost400Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const RemoveLikeFromPost401Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const RemoveLikeFromPost403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const RemoveLikeFromPost404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const RemoveLikeFromPost500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

/**
 * Get all likes for a post. Current user's like status is determined from authentication context.
 * @summary Get likes for post
 */

export const GetLikesForPostParams = zod
    .object({
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .describe("Board identifier"),
        post_id: zod.number().min(1).describe("Post identifier"),
    })
    .strict();

export const GetLikesForPost200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        likes_count: zod.number().nullish(),
        likes: zod
            .array(
                zod
                    .object({
                        like_id: zod.number().optional().describe("Unique like identifier"),
                        post_id: zod.number().optional(),
                        comment_id: zod.number().nullish(),
                        user_id: zod.number().optional().describe("User nickname for display (nick_name field)"),
                        created_at: zod.string().datetime({ offset: true }).optional(),
                        updated_at: zod.string().datetime({ offset: true }).optional(),
                    })
                    .strict(),
            )
            .optional(),
        is_liked_by_current_user: zod
            .union([zod.literal(0), zod.literal(1)])
            .optional()
            .describe("Whether the current user has liked this post"),
        current_user_id: zod
            .number()
            .nullish()
            .describe("ID of the current user; null for guests (this GET doesn't require authentication)"),
    })
    .strict();

/**
 * Used by the rank selection popup to disable already-used ranks (admin/editor only).
 * @summary Get recommend meta (used ranks, current rank)
 */

export const GetRecommendMetaForPostParams = zod
    .object({
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .describe("Board identifier"),
        post_id: zod.number().min(1).describe("Post identifier"),
    })
    .strict();

export const getRecommendMetaForPost200ResponseCurrentRankMax = 15;

export const GetRecommendMetaForPost200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .optional(),
        post_id: zod.number().optional(),
        max_rank: zod.number().optional(),
        current_rank: zod.number().min(1).max(getRecommendMetaForPost200ResponseCurrentRankMax).nullish(),
        used_ranks: zod.array(zod.number()).optional(),
    })
    .strict();

/**
 * Set or update recommend rank. Rank can be null (admin/editor only).
 * @summary Set/update recommend rank
 */

export const UpsertRecommendForPostParams = zod
    .object({
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .describe("Board identifier"),
        post_id: zod.number().min(1).describe("Post identifier"),
    })
    .strict();

export const upsertRecommendForPostBodyRankMax = 15;

export const UpsertRecommendForPostBody = zod
    .object({
        rank: zod.number().min(1).max(upsertRecommendForPostBodyRankMax).optional(),
    })
    .strict();

export const upsertRecommendForPost200ResponseRankMax = 15;

export const UpsertRecommendForPost200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .optional(),
        post_id: zod.number().optional(),
        rank: zod.number().min(1).max(upsertRecommendForPost200ResponseRankMax).optional(),
    })
    .strict();

export const UpsertRecommendForPost422Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        message: zod.string().optional(),
        status_code: zod.literal(422).optional(),
        errors: zod.record(zod.string(), zod.array(zod.string())).optional(),
    })
    .strict();

/**
 * Remove recommend entry for the post (admin/editor only).
 * @summary Unset recommend
 */

export const RemoveRecommendForPostParams = zod
    .object({
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .describe("Board identifier"),
        post_id: zod.number().min(1).describe("Post identifier"),
    })
    .strict();

export const RemoveRecommendForPost200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .optional(),
        post_id: zod.number().optional(),
    })
    .strict();

/**
 * @summary Get comments for post
 */

export const GetCommentsForPostParams = zod
    .object({
        board_id: zod.enum([
            "review",
            "tiktoks",
            "beauty_news",
            "editorial_reviews",
            "expert_reviews",
            "activities",
            "test_modal",
            "test_modeless",
            "guidance",
        ]),
        post_id: zod.number().min(1),
    })
    .strict();

export const getCommentsForPostQueryPageDefault = 1;

export const GetCommentsForPostQueryParams = zod
    .object({
        page: zod.number().default(getCommentsForPostQueryPageDefault),
    })
    .strict();

export const getCommentsForPost200ResponseDataItemUserDisplayNameMax = 50;

export const getCommentsForPost200ResponseDataItemUserFirstNameMax = 100;

export const getCommentsForPost200ResponseDataItemUserLastNameMax = 100;

export const getCommentsForPost200ResponseDataItemUserNickNameMax = 50;

export const getCommentsForPost200ResponseDataItemUserPreferredPriceRangeMax = 50;

export const getCommentsForPost200ResponseDataItemUserSocialInstagramMax = 100;

export const getCommentsForPost200ResponseDataItemUserSocialFacebookMax = 100;

export const getCommentsForPost200ResponseDataItemUserSocialXMax = 100;

export const getCommentsForPost200ResponseDataItemUserSocialTiktokMax = 100;

export const getCommentsForPost200ResponseDataItemUserSocialYoutubeMax = 100;

export const getCommentsForPost200ResponseDataItemUserSocialWebsiteMax = 500;

export const getCommentsForPost200ResponseDataItemUserAvatarMax = 255;

export const GetCommentsForPost200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        data: zod.array(
            zod
                .object({
                    comment_id: zod.number().optional(),
                    post_id: zod.number().optional(),
                    board_id: zod
                        .enum([
                            "review",
                            "tiktoks",
                            "beauty_news",
                            "editorial_reviews",
                            "expert_reviews",
                            "activities",
                            "test_modal",
                            "test_modeless",
                            "guidance",
                        ])
                        .optional(),
                    user_id: zod.number().optional(),
                    content: zod.string().optional(),
                    parent_comment_id: zod
                        .number()
                        .nullish()
                        .describe("Parent comment ID (null allowed in implementation for root comments)"),
                    replies_count: zod.number().optional(),
                    like_count: zod.number().optional().describe("Deprecated - use likes_count instead"),
                    likes_count: zod.number().nullish().describe("Total number of likes for this comment"),
                    is_liked: zod.boolean().optional().describe("Whether current user has liked this comment"),
                    likes: zod
                        .array(
                            zod
                                .object({
                                    like_id: zod.number().optional().describe("Unique like identifier"),
                                    post_id: zod.number().optional(),
                                    comment_id: zod.number().nullish(),
                                    user_id: zod
                                        .number()
                                        .optional()
                                        .describe("User nickname for display (nick_name field)"),
                                    created_at: zod.string().datetime({ offset: true }).optional(),
                                    updated_at: zod.string().datetime({ offset: true }).optional(),
                                })
                                .strict(),
                        )
                        .optional()
                        .describe("Detailed likes with user info"),
                    replies: zod
                        .array(zod.object({}).passthrough())
                        .optional()
                        .describe("Nested child comments for this comment"),
                    delete_flag: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                    created_at: zod.string().datetime({ offset: true }).optional(),
                    updated_at: zod.string().datetime({ offset: true }).optional(),
                    user: zod
                        .object({
                            user_id: zod.number().optional(),
                            display_name: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserDisplayNameMax)
                                .optional()
                                .describe("Full display name"),
                            first_name: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserFirstNameMax)
                                .optional()
                                .describe("First name"),
                            last_name: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserLastNameMax)
                                .optional()
                                .describe("Last name"),
                            nick_name: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserNickNameMax)
                                .optional()
                                .describe("Nickname or username"),
                            date_of_birth: zod.string().date().nullish().describe("Date of birth in YYYY-MM-DD format"),
                            gender: zod
                                .enum(["male", "female", "other", "prefer_not_to_say"])
                                .nullish()
                                .describe("Gender identity (optional for public display)"),
                            skin_type: zod
                                .enum([
                                    "normal",
                                    "oily",
                                    "dry",
                                    "combination",
                                    "sensitive",
                                    "dehydrated_oily",
                                    "not_sure",
                                ])
                                .nullish()
                                .describe("Skin type (preference profile)"),
                            skin_concerns: zod
                                .array(zod.string())
                                .optional()
                                .describe("Skin concerns (multiple selection)"),
                            skin_tone: zod.enum(["very_fair", "fair", "medium", "tan", "deep", "not_sure"]).nullish(),
                            hair_type: zod
                                .enum([
                                    "straight_coarse",
                                    "straight_soft",
                                    "wavy_coarse",
                                    "wavy_soft",
                                    "normal_hair",
                                    "not_sure",
                                ])
                                .nullish(),
                            makeup_frequency: zod.enum(["every_day", "occasionally", "rarely"]).nullish(),
                            hair_scalp_concerns: zod
                                .array(zod.string())
                                .optional()
                                .describe("Hair & scalp concerns (multiple selection)"),
                            beauty_wellness_interests: zod
                                .array(zod.string())
                                .optional()
                                .describe("Beauty & wellness interests (multiple selection)"),
                            lifestyle_environment: zod
                                .array(zod.string())
                                .optional()
                                .describe("Lifestyle environment (multiple selection)"),
                            preferred_price_range: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserPreferredPriceRangeMax)
                                .optional()
                                .describe("Preferred price range (single select)"),
                            social_instagram: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserSocialInstagramMax)
                                .optional(),
                            social_facebook: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserSocialFacebookMax)
                                .optional(),
                            social_x: zod.string().max(getCommentsForPost200ResponseDataItemUserSocialXMax).optional(),
                            social_tiktok: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserSocialTiktokMax)
                                .optional(),
                            social_youtube: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserSocialYoutubeMax)
                                .optional(),
                            social_website: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserSocialWebsiteMax)
                                .optional(),
                            posts_count: zod.number().nullish(),
                            review_count: zod.number().nullish(),
                            followers_count: zod.number().optional(),
                            followings_count: zod.number().optional(),
                            recommend_rank: zod.number().nullish(),
                            avatar: zod
                                .string()
                                .max(getCommentsForPost200ResponseDataItemUserAvatarMax)
                                .optional()
                                .describe("Avatar image path"),
                            role: zod
                                .enum(["admin", "editor", "moderator", "expert", "user"])
                                .optional()
                                .describe("User role"),
                            created_at: zod.string().datetime({ offset: true }).optional(),
                        })
                        .strict()
                        .optional()
                        .describe("User information for comment author"),
                })
                .strict(),
        ),
        pagination: zod
            .object({
                current_page: zod.number().optional(),
                total: zod.number().optional(),
                per_page: zod.number().optional(),
                last_page: zod.number().optional(),
            })
            .strict(),
    })
    .strict();

/**
 * @summary Add comment to post
 */

export const AddCommentToPostParams = zod
    .object({
        board_id: zod.enum([
            "review",
            "tiktoks",
            "beauty_news",
            "editorial_reviews",
            "expert_reviews",
            "activities",
            "test_modal",
            "test_modeless",
            "guidance",
        ]),
        post_id: zod.number().min(1),
    })
    .strict();

export const addCommentToPostBodyContentMax = 1000;

export const AddCommentToPostBody = zod
    .object({
        content: zod.string().min(1).max(addCommentToPostBodyContentMax),
        parent_comment_id: zod
            .number()
            .nullish()
            .describe("For nested replies (optional, null allowed in implementation)"),
    })
    .strict();

export const AddCommentToPost201Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        comment_id: zod.number().optional().describe("Newly created comment ID for rendering"),
    })
    .strict();

export const AddCommentToPost400Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const AddCommentToPost403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const AddCommentToPost404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const AddCommentToPost422Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Validation error message"),
        errors: zod.record(zod.string(), zod.array(zod.string())).optional().describe("Field validation messages"),
        status_code: zod.literal(422).describe("HTTP status code"),
    })
    .strict();

/**
 * Update an existing comment. User can only update their own comments.
 * @summary Update comment
 */

export const UpdateCommentParams = zod
    .object({
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .describe("Board identifier"),
        post_id: zod.number().min(1).describe("Post identifier"),
        comment_id: zod.number().min(1).describe("Comment identifier"),
    })
    .strict();

export const updateCommentBodyContentMax = 1000;

export const UpdateCommentBody = zod
    .object({
        content: zod.string().min(1).max(updateCommentBodyContentMax).describe("Updated comment content"),
    })
    .strict();

export const updateComment200ResponseCommentUserDisplayNameMax = 50;

export const updateComment200ResponseCommentUserFirstNameMax = 100;

export const updateComment200ResponseCommentUserLastNameMax = 100;

export const updateComment200ResponseCommentUserNickNameMax = 50;

export const updateComment200ResponseCommentUserPreferredPriceRangeMax = 50;

export const updateComment200ResponseCommentUserSocialInstagramMax = 100;

export const updateComment200ResponseCommentUserSocialFacebookMax = 100;

export const updateComment200ResponseCommentUserSocialXMax = 100;

export const updateComment200ResponseCommentUserSocialTiktokMax = 100;

export const updateComment200ResponseCommentUserSocialYoutubeMax = 100;

export const updateComment200ResponseCommentUserSocialWebsiteMax = 500;

export const updateComment200ResponseCommentUserAvatarMax = 255;

export const UpdateComment200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        comment: zod
            .object({
                comment_id: zod.number().optional(),
                post_id: zod.number().optional(),
                board_id: zod
                    .enum([
                        "review",
                        "tiktoks",
                        "beauty_news",
                        "editorial_reviews",
                        "expert_reviews",
                        "activities",
                        "test_modal",
                        "test_modeless",
                        "guidance",
                    ])
                    .optional(),
                user_id: zod.number().optional(),
                content: zod.string().optional(),
                parent_comment_id: zod
                    .number()
                    .nullish()
                    .describe("Parent comment ID (null allowed in implementation for root comments)"),
                replies_count: zod.number().optional(),
                like_count: zod.number().optional().describe("Deprecated - use likes_count instead"),
                likes_count: zod.number().nullish().describe("Total number of likes for this comment"),
                is_liked: zod.boolean().optional().describe("Whether current user has liked this comment"),
                likes: zod
                    .array(
                        zod
                            .object({
                                like_id: zod.number().optional().describe("Unique like identifier"),
                                post_id: zod.number().optional(),
                                comment_id: zod.number().nullish(),
                                user_id: zod
                                    .number()
                                    .optional()
                                    .describe("User nickname for display (nick_name field)"),
                                created_at: zod.string().datetime({ offset: true }).optional(),
                                updated_at: zod.string().datetime({ offset: true }).optional(),
                            })
                            .strict(),
                    )
                    .optional()
                    .describe("Detailed likes with user info"),
                replies: zod
                    .array(zod.object({}).passthrough())
                    .optional()
                    .describe("Nested child comments for this comment"),
                delete_flag: zod.union([zod.literal(0), zod.literal(1)]).optional(),
                created_at: zod.string().datetime({ offset: true }).optional(),
                updated_at: zod.string().datetime({ offset: true }).optional(),
                user: zod
                    .object({
                        user_id: zod.number().optional(),
                        display_name: zod
                            .string()
                            .max(updateComment200ResponseCommentUserDisplayNameMax)
                            .optional()
                            .describe("Full display name"),
                        first_name: zod
                            .string()
                            .max(updateComment200ResponseCommentUserFirstNameMax)
                            .optional()
                            .describe("First name"),
                        last_name: zod
                            .string()
                            .max(updateComment200ResponseCommentUserLastNameMax)
                            .optional()
                            .describe("Last name"),
                        nick_name: zod
                            .string()
                            .max(updateComment200ResponseCommentUserNickNameMax)
                            .optional()
                            .describe("Nickname or username"),
                        date_of_birth: zod.string().date().nullish().describe("Date of birth in YYYY-MM-DD format"),
                        gender: zod
                            .enum(["male", "female", "other", "prefer_not_to_say"])
                            .nullish()
                            .describe("Gender identity (optional for public display)"),
                        skin_type: zod
                            .enum(["normal", "oily", "dry", "combination", "sensitive", "dehydrated_oily", "not_sure"])
                            .nullish()
                            .describe("Skin type (preference profile)"),
                        skin_concerns: zod
                            .array(zod.string())
                            .optional()
                            .describe("Skin concerns (multiple selection)"),
                        skin_tone: zod.enum(["very_fair", "fair", "medium", "tan", "deep", "not_sure"]).nullish(),
                        hair_type: zod
                            .enum([
                                "straight_coarse",
                                "straight_soft",
                                "wavy_coarse",
                                "wavy_soft",
                                "normal_hair",
                                "not_sure",
                            ])
                            .nullish(),
                        makeup_frequency: zod.enum(["every_day", "occasionally", "rarely"]).nullish(),
                        hair_scalp_concerns: zod
                            .array(zod.string())
                            .optional()
                            .describe("Hair & scalp concerns (multiple selection)"),
                        beauty_wellness_interests: zod
                            .array(zod.string())
                            .optional()
                            .describe("Beauty & wellness interests (multiple selection)"),
                        lifestyle_environment: zod
                            .array(zod.string())
                            .optional()
                            .describe("Lifestyle environment (multiple selection)"),
                        preferred_price_range: zod
                            .string()
                            .max(updateComment200ResponseCommentUserPreferredPriceRangeMax)
                            .optional()
                            .describe("Preferred price range (single select)"),
                        social_instagram: zod
                            .string()
                            .max(updateComment200ResponseCommentUserSocialInstagramMax)
                            .optional(),
                        social_facebook: zod
                            .string()
                            .max(updateComment200ResponseCommentUserSocialFacebookMax)
                            .optional(),
                        social_x: zod.string().max(updateComment200ResponseCommentUserSocialXMax).optional(),
                        social_tiktok: zod.string().max(updateComment200ResponseCommentUserSocialTiktokMax).optional(),
                        social_youtube: zod
                            .string()
                            .max(updateComment200ResponseCommentUserSocialYoutubeMax)
                            .optional(),
                        social_website: zod
                            .string()
                            .max(updateComment200ResponseCommentUserSocialWebsiteMax)
                            .optional(),
                        posts_count: zod.number().nullish(),
                        review_count: zod.number().nullish(),
                        followers_count: zod.number().optional(),
                        followings_count: zod.number().optional(),
                        recommend_rank: zod.number().nullish(),
                        avatar: zod
                            .string()
                            .max(updateComment200ResponseCommentUserAvatarMax)
                            .optional()
                            .describe("Avatar image path"),
                        role: zod
                            .enum(["admin", "editor", "moderator", "expert", "user"])
                            .optional()
                            .describe("User role"),
                        created_at: zod.string().datetime({ offset: true }).optional(),
                    })
                    .strict()
                    .optional()
                    .describe("User information for comment author"),
            })
            .strict()
            .optional(),
    })
    .strict();

export const UpdateComment400Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const UpdateComment403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const UpdateComment404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const UpdateComment422Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Validation error message"),
        errors: zod.record(zod.string(), zod.array(zod.string())).optional().describe("Field validation messages"),
        status_code: zod.literal(422).describe("HTTP status code"),
    })
    .strict();

export const UpdateComment500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

/**
 * Delete a comment. User can only delete their own comments or admins can delete any comment.
 * @summary Delete comment
 */

export const DeleteCommentParams = zod
    .object({
        board_id: zod
            .enum([
                "review",
                "tiktoks",
                "beauty_news",
                "editorial_reviews",
                "expert_reviews",
                "activities",
                "test_modal",
                "test_modeless",
                "guidance",
            ])
            .describe("Board identifier"),
        post_id: zod.number().min(1).describe("Post identifier"),
        comment_id: zod.number().min(1).describe("Comment identifier"),
    })
    .strict();

export const DeleteComment200Response = zod
    .object({
        success: zod
            .union([zod.literal(0), zod.literal(1)])
            .optional()
            .describe("Success flag: 1 = success, 0 = failure"),
    })
    .strict();

export const DeleteComment400Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const DeleteComment403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const DeleteComment404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

export const DeleteComment500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
    })
    .strict();

/**
 * Add a like to a specific comment. User ID is obtained from authentication context.
 * @summary Add like to comment
 */

export const AddLikeToCommentParams = zod
    .object({
        board_id: zod.enum([
            "review",
            "tiktoks",
            "beauty_news",
            "editorial_reviews",
            "expert_reviews",
            "activities",
            "test_modal",
            "test_modeless",
            "guidance",
        ]),
        post_id: zod.number().min(1),
        comment_id: zod.number().min(1),
    })
    .strict();

export const AddLikeToComment201Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        likes_count: zod.number().nullish(),
    })
    .strict();

export const AddLikeToComment400Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const AddLikeToComment401Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const AddLikeToComment403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const AddLikeToComment404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const AddLikeToComment500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

/**
 * Remove a like from a specific comment. User ID is obtained from authentication context.
 * @summary Remove like from comment
 */

export const RemoveLikeFromCommentParams = zod
    .object({
        board_id: zod.enum([
            "review",
            "tiktoks",
            "beauty_news",
            "editorial_reviews",
            "expert_reviews",
            "activities",
            "test_modal",
            "test_modeless",
            "guidance",
        ]),
        post_id: zod.number().min(1),
        comment_id: zod.number().min(1),
    })
    .strict();

export const RemoveLikeFromComment200Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]).optional(),
        likes_count: zod.number().nullish(),
    })
    .strict();

export const RemoveLikeFromComment400Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const RemoveLikeFromComment401Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const RemoveLikeFromComment403Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const RemoveLikeFromComment404Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();

export const RemoveLikeFromComment500Response = zod
    .object({
        success: zod.union([zod.literal(0), zod.literal(1)]),
        message: zod.string().describe("Error message"),
        errors: zod.record(zod.string(), zod.unknown()).optional().describe("Detailed error information"),
        status_code: zod
            .union([zod.literal(400), zod.literal(401), zod.literal(403), zod.literal(404), zod.literal(500)])
            .describe("HTTP status code"),
        likes_count: zod.number().nullish().describe("Current likes count when available"),
    })
    .strict();
